// Bespoke procedural models for the twelve faction-signature production structures in the
// v0.2.4 appearance guide. Local +z is the public/front side of each building.

import * as THREE from 'three';
import { OWNER_COLORS } from './unitStyles';

type Footprint = { w: number; h: number };

export const BESPOKE_BUILDING_DEFS = new Set([
  'b-ic-barracks',
  'b-ic-longshot-foundry',
  'b-ic-siege-depot',
  'b-ah-grave-hatchery',
  'b-ah-blood-foundry',
  'b-ah-rift-altar',
  'b-vw-thorn-nursery',
  'b-vw-beast-hollow',
  'b-vw-skybloom-roost',
  'b-ad-precision-foundry',
  'b-ad-conduit-forge',
  'b-ad-starforge-dock',
  'b-ic-crown-bastion',
  'b-ic-builder-guild',
  'b-ic-oathbound-gate',
  'b-ic-ritual-standard',
  'b-ic-castle-wall',
  'b-ah-ossuary-throne',
  'b-ah-site-tower',
  'b-ah-soul-mill',
  'b-ah-devouring-gate',
  'b-ah-ritual-standard',
  'b-ah-castle-wall',
  'b-vw-living-grove',
  'b-vw-defense-grove',
  'b-vw-sapling',
  'b-vw-ritual-standard',
  'b-ad-meridian-prism',
  'b-ad-site-tower',
  'b-ad-aether-refinery',
  'b-ad-decoy',
  'b-ad-ritual-standard',
  'b-ad-castle-wall',
  // Core Annex System deployed/derived structures
  'b-vw-bramblehold',
  'b-ic-field-bastion',
  'b-ah-blood-cocoon',
  'b-ah-grave-beacon',
  'b-ad-astral-relay',
  // Dedicated deck-buildable defensive towers (generic faction silhouettes).
  'b-ic-bulwark-arbalest',
  'b-ah-wailing-obelisk',
  'b-vw-briarwatch-tower',
  'b-ad-lensing-spire',
]);

function mix(a: number, b: number, t: number): THREE.Color {
  return new THREE.Color(a).lerp(new THREE.Color(b), t);
}

function material(color: THREE.Color | number, roughness = 0.8, metalness = 0.08): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({ color, roughness, metalness, flatShading: true });
}

function luminous(color: number, intensity = 1, opacity = 1): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color,
    emissive: color,
    emissiveIntensity: intensity,
    roughness: 0.25,
    transparent: opacity < 1,
    opacity,
    depthWrite: opacity > 0.65,
    side: THREE.DoubleSide,
    flatShading: true,
  });
}

function finish(group: THREE.Group): THREE.Group {
  group.traverse((o) => {
    if (o.name === 'pulseCore') o.userData.baseScale = o.scale.clone();
    if (o instanceof THREE.Mesh) {
      o.castShadow = true;
      o.receiveShadow = true;
    }
  });
  return group;
}

function flag(owner: 0 | 1, width: number, height: number): THREE.Mesh {
  return new THREE.Mesh(
    new THREE.PlaneGeometry(width, height),
    new THREE.MeshStandardMaterial({ color: OWNER_COLORS[owner], roughness: 0.9, side: THREE.DoubleSide }),
  );
}

function pole(height: number, mat: THREE.Material): THREE.Mesh {
  return new THREE.Mesh(new THREE.CylinderGeometry(0.055, 0.07, height, 6), mat);
}

function crenels(group: THREE.Group, width: number, depth: number, y: number, mat: THREE.Material): void {
  const countX = Math.max(3, Math.round(width / 1.15));
  const countZ = Math.max(3, Math.round(depth / 1.15));
  for (let i = 0; i < countX; i++) {
    const x = -width / 2 + 0.45 + i * ((width - 0.9) / Math.max(1, countX - 1));
    for (const z of [-depth / 2, depth / 2]) {
      const tooth = new THREE.Mesh(new THREE.BoxGeometry(0.48, 0.58, 0.34), mat);
      tooth.position.set(x, y, z);
      group.add(tooth);
    }
  }
  for (let i = 1; i < countZ - 1; i++) {
    const z = -depth / 2 + 0.45 + i * ((depth - 0.9) / Math.max(1, countZ - 1));
    for (const x of [-width / 2, width / 2]) {
      const tooth = new THREE.Mesh(new THREE.BoxGeometry(0.34, 0.58, 0.48), mat);
      tooth.position.set(x, y, z);
      group.add(tooth);
    }
  }
}

function ballista(mat: THREE.Material, trim: THREE.Material): THREE.Group {
  const turret = new THREE.Group();
  turret.name = 'turret';
  const mount = new THREE.Mesh(new THREE.CylinderGeometry(0.62, 0.78, 0.38, 10), trim);
  turret.add(mount);
  const rail = new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.2, 2.5), mat);
  rail.position.set(0, 0.36, 0.45);
  turret.add(rail);
  for (const sx of [-1, 1]) {
    const arm = new THREE.Mesh(new THREE.BoxGeometry(1.25, 0.16, 0.2), mat);
    arm.position.set(sx * 0.64, 0.4, 0.95);
    arm.rotation.y = sx * -0.42;
    turret.add(arm);
  }
  const bolt = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.045, 2.1, 6), trim);
  bolt.rotation.x = Math.PI / 2;
  bolt.position.set(0, 0.48, 0.8);
  turret.add(bolt);
  return turret;
}

function ironBastion(owner: 0 | 1, fp: Footprint): THREE.Group {
  const group = new THREE.Group();
  const stone = material(mix(0xc6c1b7, OWNER_COLORS[owner], 0.08), 0.9);
  const iron = material(0x30363e, 0.48, 0.62);
  const gold = material(0xc9a650, 0.36, 0.72);
  const wood = material(0x4f3928, 0.92);
  const w = fp.w * 0.82;
  const d = fp.h * 0.76;

  // Drill yard and precise formation lines sit in front of the compact castle tower.
  const yard = new THREE.Mesh(new THREE.BoxGeometry(w, 0.12, 1.55), stone);
  yard.position.set(0, 0.06, d / 2 + 0.72);
  group.add(yard);
  for (const x of [-1.2, 0, 1.2]) {
    const line = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.025, 1.25), gold);
    line.position.set(x, 0.14, d / 2 + 0.72);
    group.add(line);
  }

  const tower = new THREE.Mesh(new THREE.BoxGeometry(w, 4.6, d), stone);
  tower.position.y = 2.3;
  group.add(tower);
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      const band = new THREE.Mesh(new THREE.BoxGeometry(0.46, 5.0, 0.46), iron);
      band.position.set(sx * (w / 2 - 0.16), 2.5, sz * (d / 2 - 0.16));
      group.add(band);
      for (let y = 0.7; y < 4.6; y += 1.15) {
        const rivet = new THREE.Mesh(new THREE.SphereGeometry(0.08, 6, 5), gold);
        rivet.position.set(sx * (w / 2 + 0.08), y, sz * (d / 2 + 0.08));
        group.add(rivet);
      }
    }
  }
  // Twin guarded deployment gates.
  for (const x of [-1.15, 1.15]) {
    const gate = new THREE.Mesh(new THREE.BoxGeometry(1.25, 1.9, 0.18), wood);
    gate.position.set(x, 0.96, d / 2 + 0.1);
    group.add(gate);
    const lintel = new THREE.Mesh(new THREE.BoxGeometry(1.55, 0.25, 0.3), iron);
    lintel.position.set(x, 1.98, d / 2 + 0.08);
    group.add(lintel);
    for (const gx of [-0.48, 0.48]) {
      const guard = new THREE.Mesh(new THREE.BoxGeometry(0.18, 1.6, 0.3), iron);
      guard.position.set(x + gx, 0.85, d / 2 + 0.18);
      group.add(guard);
    }
  }
  const parapet = new THREE.Mesh(new THREE.BoxGeometry(w + 0.35, 0.45, d + 0.35), iron);
  parapet.position.y = 4.75;
  group.add(parapet);
  crenels(group, w + 0.25, d + 0.25, 5.27, stone);
  const weapon = ballista(iron, gold);
  weapon.position.y = 5.55;
  group.add(weapon);

  // Mounting platform: empty racks visibly reserve space for a squad silhouette.
  const platform = new THREE.Mesh(new THREE.BoxGeometry(w * 0.72, 0.16, 1.05), wood);
  platform.position.set(0, 5.2, -0.55);
  group.add(platform);
  for (const x of [-1.15, -0.38, 0.38, 1.15]) {
    const rack = new THREE.Mesh(new THREE.CylinderGeometry(0.035, 0.035, 1.0, 5), iron);
    rack.position.set(x, 5.75, -0.72);
    group.add(rack);
  }

  const upgrade = new THREE.Group();
  upgrade.name = 'upgradeDeco';
  const upper = new THREE.Mesh(new THREE.BoxGeometry(w * 0.7, 1.5, d * 0.7), stone);
  upper.position.y = 5.55;
  upgrade.add(upper);
  crenels(upgrade, w * 0.72, d * 0.72, 6.55, stone);
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      const corner = new THREE.Mesh(new THREE.CylinderGeometry(0.52, 0.62, 2.25, 8), iron);
      corner.position.set(sx * w * 0.47, 5.55, sz * d * 0.47);
      upgrade.add(corner);
    }
    const p = pole(2.5, gold);
    p.position.set(sx * (w / 2 + 0.42), 6.3, 0);
    upgrade.add(p);
    const banner = flag(owner, 1.15, 0.72);
    banner.position.set(sx * (w / 2 + 0.42) + 0.55, 7.05, 0);
    upgrade.add(banner);
  }
  upgrade.visible = false;
  group.add(upgrade);
  return finish(group);
}

function longshotFoundry(owner: 0 | 1, fp: Footprint): THREE.Group {
  const group = new THREE.Group();
  const stone = material(mix(0xaaa89c, OWNER_COLORS[owner], 0.08), 0.86);
  const iron = material(0x343a42, 0.46, 0.6);
  const brass = material(0xc59a48, 0.32, 0.72);
  const wood = material(0x725035, 0.86);
  const beam = luminous(0xffcf55, 1.2, 0.72);
  const w = fp.w * 0.9;
  const d = fp.h * 0.82;

  const base = new THREE.Mesh(new THREE.BoxGeometry(w, 0.55, d), stone);
  base.position.y = 0.28;
  group.add(base);
  // Long central construction rail and moving Repeater Battery carriage.
  for (const x of [-0.42, 0.42]) {
    const rail = new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.2, d * 0.9), iron);
    rail.position.set(x, 0.66, 0);
    group.add(rail);
  }
  const carriage = new THREE.Group();
  carriage.name = 'assemblyCarriage';
  const bed = new THREE.Mesh(new THREE.BoxGeometry(1.45, 0.35, 1.2), wood);
  bed.position.y = 0.86;
  carriage.add(bed);
  for (let i = 0; i < 4; i++) {
    const limb = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.12, 1.35), iron);
    limb.position.set((i - 1.5) * 0.32, 1.18, 0.08);
    limb.rotation.z = (i - 1.5) * 0.08;
    carriage.add(limb);
  }
  group.add(carriage);

  // Narrow workshop shells leave the precision machinery exposed.
  for (const x of [-w / 2 + 0.6, w / 2 - 0.6]) {
    const shop = new THREE.Mesh(new THREE.BoxGeometry(1.05, 2.3, d * 0.9), stone);
    shop.position.set(x, 1.5, 0);
    group.add(shop);
    const roof = new THREE.Mesh(new THREE.BoxGeometry(1.28, 0.22, d), iron);
    roof.position.set(x, 2.72, 0);
    group.add(roof);
  }
  // Mirror/lens arrays align along the rail.
  const spinner = new THREE.Group();
  spinner.name = 'spinner';
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2;
    const mast = pole(2.5 + (i % 2) * 0.55, iron);
    mast.position.set(Math.cos(a) * 1.75, 1.7, Math.sin(a) * 1.75);
    spinner.add(mast);
    const lens = new THREE.Mesh(new THREE.TorusGeometry(0.36, 0.07, 7, 16), brass);
    lens.position.set(Math.cos(a) * 1.75, 3.0 + (i % 2) * 0.55, Math.sin(a) * 1.75);
    lens.rotation.y = -a;
    spinner.add(lens);
  }
  group.add(spinner);
  const sightline = new THREE.Mesh(new THREE.BoxGeometry(0.035, 0.025, d + 3.2), beam);
  sightline.position.set(0, 0.82, 1.4);
  group.add(sightline);

  const upgrade = new THREE.Group();
  upgrade.name = 'upgradeDeco';
  for (const x of [-1.45, 1.45]) {
    const shield = new THREE.Mesh(new THREE.BoxGeometry(0.3, 1.45, 2.25), iron);
    shield.position.set(x, 1.25, 0);
    upgrade.add(shield);
  }
  const crown = new THREE.Group();
  crown.name = 'spinnerReverse';
  crown.position.y = 4.15;
  for (let i = 0; i < 3; i++) {
    const ring = new THREE.Mesh(new THREE.TorusGeometry(0.72 + i * 0.22, 0.07, 7, 20), i === 2 ? beam : brass);
    ring.rotation.x = Math.PI / 2;
    crown.add(ring);
  }
  upgrade.add(crown);
  upgrade.visible = false;
  group.add(upgrade);
  return finish(group);
}

function siegeDepot(owner: 0 | 1, fp: Footprint): THREE.Group {
  const group = new THREE.Group();
  const stone = material(mix(0x9d9c96, OWNER_COLORS[owner], 0.08), 0.88);
  const iron = material(0x30363e, 0.48, 0.62);
  const gold = material(0xc6a34f, 0.36, 0.7);
  const wood = material(0x654832, 0.9);
  const rune = luminous(0x8fd7ff, 1.1, 0.8);
  const w = fp.w * 0.9;
  const d = fp.h * 0.84;
  const base = new THREE.Mesh(new THREE.BoxGeometry(w, 0.45, d), stone);
  base.position.y = 0.23;
  group.add(base);

  // Three distinct production bays: cannon, mortar, and Aegis equipment.
  const bayXs = [-1.65, 0, 1.65];
  for (let i = 0; i < 3; i++) {
    const shell = new THREE.Mesh(new THREE.BoxGeometry(1.45, 2.25, d * 0.72), stone);
    shell.position.set(bayXs[i], 1.45, -0.25);
    group.add(shell);
    const opening = new THREE.Mesh(new THREE.BoxGeometry(1.08, 1.5, 0.12), iron);
    opening.position.set(bayXs[i], 1.0, d * 0.37);
    group.add(opening);
  }
  // Cannon bay.
  const cannon = new THREE.Mesh(new THREE.CylinderGeometry(0.13, 0.19, 1.65, 8), iron);
  cannon.rotation.x = Math.PI / 2;
  cannon.position.set(bayXs[0], 0.9, 1.55);
  group.add(cannon);
  for (const x of [bayXs[0] - 0.46, bayXs[0] + 0.46]) {
    const wheel = new THREE.Mesh(new THREE.CylinderGeometry(0.42, 0.42, 0.12, 10), wood);
    wheel.rotation.z = Math.PI / 2;
    wheel.position.set(x, 0.55, 1.1);
    group.add(wheel);
  }
  // Mortar bay.
  const mortar = new THREE.Mesh(new THREE.CylinderGeometry(0.24, 0.36, 1.15, 9), iron);
  mortar.rotation.x = -0.62;
  mortar.position.set(0, 1.05, 1.25);
  group.add(mortar);
  // Aegis bay.
  for (let i = -1; i <= 1; i++) {
    const plate = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.72, 0.08), rune);
    plate.position.set(bayXs[2] + i * 0.42, 1.12 + Math.abs(i) * 0.18, 1.55 - Math.abs(i) * 0.12);
    plate.rotation.y = i * -0.24;
    group.add(plate);
  }
  // Shared overhead repair gantry with chains and welding arms.
  const gantry = new THREE.Group();
  gantry.name = 'pump';
  for (const x of [-w / 2 + 0.28, w / 2 - 0.28]) {
    const post = new THREE.Mesh(new THREE.BoxGeometry(0.24, 3.8, 0.28), iron);
    post.position.set(x, 2.1, 0);
    gantry.add(post);
  }
  const cross = new THREE.Mesh(new THREE.BoxGeometry(w, 0.28, 0.34), iron);
  cross.position.y = 4.0;
  gantry.add(cross);
  for (const x of [-1.7, 0, 1.7]) {
    const chain = pole(1.25, iron);
    chain.position.set(x, 3.35, 0.2);
    gantry.add(chain);
    const hook = new THREE.Mesh(new THREE.TorusGeometry(0.18, 0.05, 6, 12, Math.PI * 1.45), gold);
    hook.position.set(x, 2.68, 0.2);
    gantry.add(hook);
  }
  group.add(gantry);

  const upgrade = new THREE.Group();
  upgrade.name = 'upgradeDeco';
  for (let i = 0; i < 3; i++) {
    const seal = new THREE.Mesh(new THREE.CylinderGeometry(0.3, 0.3, 0.08, 12), gold);
    seal.rotation.x = Math.PI / 2;
    seal.position.set(bayXs[i], 2.45, d * 0.43);
    upgrade.add(seal);
  }
  for (const x of [-2.15, 2.15]) {
    const p = pole(2.4, gold);
    p.position.set(x, 4.8, -1.25);
    upgrade.add(p);
    const banner = flag(owner, 1.05, 0.65);
    banner.position.set(x + 0.52, 5.48, -1.25);
    upgrade.add(banner);
  }
  for (let i = 0; i < 5; i++) {
    const shell = new THREE.Mesh(new THREE.CylinderGeometry(0.11, 0.14, 0.72, 7), gold);
    shell.position.set(-2.15 + i * 0.28, 0.83, -d / 2 - 0.22);
    upgrade.add(shell);
  }
  upgrade.visible = false;
  group.add(upgrade);
  return finish(group);
}

function boneRib(mat: THREE.Material, radius: number, height: number): THREE.Mesh {
  const rib = new THREE.Mesh(new THREE.TorusGeometry(radius, 0.1, 7, 18, Math.PI), mat);
  rib.rotation.x = Math.PI / 2;
  rib.rotation.z = Math.PI / 2;
  rib.scale.y = height / Math.max(0.1, radius * 2);
  return rib;
}

function graveHatchery(_owner: 0 | 1, fp: Footprint): THREE.Group {
  const group = new THREE.Group();
  const bone = material(0xb9b09f, 0.92);
  const blackBone = material(0x2c292e, 0.82, 0.18);
  const coffin = material(0x4b3329, 0.95);
  const soul = luminous(0xc63838, 1.25, 0.62);
  const r = Math.min(fp.w, fp.h) * 0.4;
  const nest = new THREE.Mesh(new THREE.CylinderGeometry(r, r * 1.08, 0.55, 14), material(0x30252a, 1));
  nest.position.y = 0.28;
  group.add(nest);
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    const rib = boneRib(i % 2 ? bone : blackBone, 1.05, 2.8);
    rib.position.set(Math.cos(a) * r * 0.72, 1.12, Math.sin(a) * r * 0.72);
    rib.rotation.y = -a;
    group.add(rib);
    if (i % 2 === 0) {
      const c = new THREE.Mesh(new THREE.BoxGeometry(0.72, 0.24, 1.35), coffin);
      c.position.set(Math.cos(a) * (r + 0.28), 0.22, Math.sin(a) * (r + 0.28));
      c.rotation.y = -a;
      group.add(c);
    }
  }
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * Math.PI * 2;
    const sac = new THREE.Mesh(new THREE.SphereGeometry(0.48 + (i % 2) * 0.12, 10, 8), soul);
    sac.name = i === 0 ? 'pulseCore' : '';
    sac.scale.y = 1.35;
    sac.position.set(Math.cos(a) * 1.05, 0.86, Math.sin(a) * 1.05);
    group.add(sac);
    const claw = new THREE.Mesh(new THREE.ConeGeometry(0.07, 0.36, 5), blackBone);
    claw.position.copy(sac.position).add(new THREE.Vector3(0.12, 0.12, 0.42));
    claw.rotation.x = 1.0;
    group.add(claw);
  }
  const clock = new THREE.Group();
  clock.name = 'spinner';
  clock.position.y = 1.05;
  const dial = new THREE.Mesh(new THREE.TorusGeometry(0.82, 0.1, 7, 18), bone);
  dial.rotation.x = Math.PI / 2;
  clock.add(dial);
  for (const angle of [0, Math.PI / 2]) {
    const hand = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.06, 0.72), soul);
    hand.rotation.y = angle;
    hand.position.z = 0.3;
    clock.add(hand);
  }
  group.add(clock);
  const upgrade = new THREE.Group();
  upgrade.name = 'upgradeDeco';
  const teeth = new THREE.Group();
  teeth.name = 'spinnerReverse';
  teeth.position.y = 1.45;
  for (let i = 0; i < 12; i++) {
    const a = (i / 12) * Math.PI * 2;
    const tooth = new THREE.Mesh(new THREE.ConeGeometry(0.1, 0.45, 5), bone);
    tooth.position.set(Math.cos(a) * 1.35, 0, Math.sin(a) * 1.35);
    tooth.rotation.z = Math.PI;
    teeth.add(tooth);
  }
  upgrade.add(teeth);
  upgrade.visible = false;
  group.add(upgrade);
  return finish(group);
}

function bloodFoundry(_owner: 0 | 1, fp: Footprint): THREE.Group {
  const group = new THREE.Group();
  const flesh = material(0x6f3036, 0.88);
  const bone = material(0xb8aa96, 0.9);
  const rust = material(0x5a3428, 0.7, 0.35);
  const blood = luminous(0xb51f2f, 1.2, 0.48);
  const base = new THREE.Mesh(new THREE.CylinderGeometry(fp.w * 0.38, fp.w * 0.44, 0.65, 12), flesh);
  base.position.y = 0.33;
  group.add(base);
  const vat = new THREE.Mesh(new THREE.CylinderGeometry(1.32, 1.55, 4.45, 14, 1, true), blood);
  vat.name = 'pulseCore';
  vat.position.y = 2.75;
  group.add(vat);
  const floor = new THREE.Mesh(new THREE.CylinderGeometry(1.5, 1.5, 0.28, 14), rust);
  floor.position.y = 0.62;
  group.add(floor);
  const rim = new THREE.Mesh(new THREE.TorusGeometry(1.48, 0.16, 8, 18), rust);
  rim.rotation.x = Math.PI / 2;
  rim.position.y = 4.95;
  group.add(rim);
  // Developing Mauler silhouette inside the translucent vat.
  const torso = new THREE.Mesh(new THREE.CapsuleGeometry(0.55, 1.55, 5, 9), material(0x3a2025, 0.9));
  torso.position.y = 2.75;
  group.add(torso);
  for (const sx of [-1, 1]) {
    const arm = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.3, 1.75, 7), material(0x45242a, 0.9));
    arm.position.set(sx * 0.7, 2.7, 0);
    arm.rotation.z = sx * 0.48;
    group.add(arm);
  }
  // Pipes, nutrient tubes, hooks, and sacrificial chute.
  const pump = new THREE.Group();
  pump.name = 'pump';
  for (const sx of [-1, 1]) {
    const pipe = new THREE.Mesh(new THREE.CylinderGeometry(0.15, 0.2, 3.2, 7), rust);
    pipe.position.set(sx * 1.72, 2.0, 0.2);
    pump.add(pipe);
    const bulb = new THREE.Mesh(new THREE.SphereGeometry(0.32, 8, 6), flesh);
    bulb.position.set(sx * 1.72, 3.4, 0.2);
    pump.add(bulb);
  }
  group.add(pump);
  const chute = new THREE.Mesh(new THREE.BoxGeometry(1.0, 0.75, 2.0), rust);
  chute.position.set(0, 1.1, 1.85);
  chute.rotation.x = -0.24;
  group.add(chute);
  const upgrade = new THREE.Group();
  upgrade.name = 'upgradeDeco';
  for (let i = 0; i < 7; i++) {
    const a = (i / 7) * Math.PI * 2;
    const rib = boneRib(bone, 0.9, 3.5);
    rib.position.set(Math.cos(a) * 1.25, 2.75, Math.sin(a) * 1.25);
    rib.rotation.y = -a;
    upgrade.add(rib);
    const thorn = new THREE.Mesh(new THREE.ConeGeometry(0.1, 0.7, 5), bone);
    thorn.position.set(Math.cos(a) * 1.7, 3.15, Math.sin(a) * 1.7);
    thorn.rotation.z = Math.cos(a) * 0.9;
    upgrade.add(thorn);
  }
  upgrade.visible = false;
  group.add(upgrade);
  return finish(group);
}

function riftAltar(_owner: 0 | 1, fp: Footprint): THREE.Group {
  const group = new THREE.Group();
  const stone = material(0x242229, 0.85, 0.18);
  const bone = material(0xb7ae9d, 0.9);
  const soul = luminous(0xb4253c, 1.55, 0.7);
  for (let i = 0; i < 3; i++) {
    const dais = new THREE.Mesh(new THREE.CylinderGeometry(fp.w * (0.42 - i * 0.07), fp.w * (0.45 - i * 0.07), 0.32, 12), i % 2 ? bone : stone);
    dais.position.y = 0.16 + i * 0.3;
    group.add(dais);
  }
  const tear = new THREE.Mesh(new THREE.PlaneGeometry(1.15, 4.6), soul);
  tear.name = 'pulseCore';
  tear.position.y = 3.0;
  group.add(tear);
  const tearCross = new THREE.Mesh(new THREE.PlaneGeometry(1.15, 4.6), soul);
  tearCross.position.y = 3.0;
  tearCross.rotation.y = Math.PI / 2;
  group.add(tearCross);
  const orbit = new THREE.Group();
  orbit.name = 'spinner';
  orbit.position.y = 2.85;
  for (let i = 0; i < 7; i++) {
    const a = (i / 7) * Math.PI * 2;
    const skull = new THREE.Mesh(new THREE.SphereGeometry(0.22, 7, 6), bone);
    skull.position.set(Math.cos(a) * 1.55, Math.sin(a * 2) * 0.45, Math.sin(a) * 1.55);
    orbit.add(skull);
    const chain = new THREE.Mesh(new THREE.TorusGeometry(1.55, 0.035, 5, 24), soul);
    chain.rotation.x = Math.PI / 2;
    orbit.add(chain);
  }
  group.add(orbit);
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * Math.PI * 2;
    const grave = new THREE.Mesh(new THREE.BoxGeometry(0.42, 0.9, 0.18), stone);
    grave.position.set(Math.cos(a) * 1.75, 0.85, Math.sin(a) * 1.75);
    grave.rotation.y = -a;
    group.add(grave);
  }
  const upgrade = new THREE.Group();
  upgrade.name = 'upgradeDeco';
  const crown = new THREE.Group();
  crown.name = 'spinnerReverse';
  crown.position.y = 5.15;
  for (let i = 0; i < 9; i++) {
    const a = (i / 9) * Math.PI * 2;
    const rib = new THREE.Mesh(new THREE.ConeGeometry(0.1, 1.05, 5), bone);
    rib.position.set(Math.cos(a) * 1.1, 0, Math.sin(a) * 1.1);
    rib.rotation.z = Math.cos(a) * 0.72;
    crown.add(rib);
  }
  upgrade.add(crown);
  upgrade.visible = false;
  group.add(upgrade);
  return finish(group);
}

function flower(mat: THREE.Material, glowMat: THREE.Material, radius: number): THREE.Group {
  const blossom = new THREE.Group();
  const center = new THREE.Mesh(new THREE.SphereGeometry(radius * 0.42, 9, 7), glowMat);
  blossom.add(center);
  for (let i = 0; i < 7; i++) {
    const a = (i / 7) * Math.PI * 2;
    const petal = new THREE.Mesh(new THREE.SphereGeometry(radius * 0.42, 8, 6), mat);
    petal.scale.set(1.45, 0.42, 0.72);
    petal.position.set(Math.cos(a) * radius * 0.56, 0, Math.sin(a) * radius * 0.56);
    petal.rotation.y = -a;
    blossom.add(petal);
  }
  return blossom;
}

function thornNursery(owner: 0 | 1, fp: Footprint): THREE.Group {
  const group = new THREE.Group();
  const bark = material(mix(0x65472e, OWNER_COLORS[owner], 0.07), 1);
  const barkLight = material(0x8a653f, 1);
  const thorn = material(0x455f2d, 0.92);
  const leaf = material(0x638b3e, 0.95);
  const petal = material(0xb75e78, 0.88);
  const sap = luminous(0xb7df63, 1.05, 0.82);
  const r = Math.min(fp.w, fp.h) * 0.39;

  // Split seed mound remains visible at the root of the mature Thornhold.
  const mound = new THREE.Mesh(new THREE.SphereGeometry(r, 10, 7), material(0x4f3d2b, 1));
  mound.scale.y = 0.32;
  mound.position.y = 0.24;
  group.add(mound);
  for (const sx of [-1, 1]) {
    const seedHalf = new THREE.Mesh(new THREE.SphereGeometry(0.8, 8, 6), barkLight);
    seedHalf.scale.set(0.72, 1.1, 0.46);
    seedHalf.position.set(sx * 0.56, 0.72, 0.65);
    seedHalf.rotation.z = sx * 0.5;
    group.add(seedHalf);
  }
  // Three intertwined trunks form a defensive living tower.
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2;
    const trunk = new THREE.Mesh(new THREE.CylinderGeometry(0.52, 0.88, 4.6, 8), i % 2 ? barkLight : bark);
    trunk.position.set(Math.cos(a) * 0.55, 2.65, Math.sin(a) * 0.55);
    trunk.rotation.z = Math.cos(a) * 0.12;
    trunk.rotation.x = Math.sin(a) * 0.12;
    group.add(trunk);
  }
  // Bark wall, bramble crown, firing blossoms, and two sheltered emergence hollows.
  const wall = new THREE.Mesh(new THREE.CylinderGeometry(r * 0.86, r, 1.55, 10, 1, true), bark);
  wall.position.y = 1.22;
  group.add(wall);
  for (const x of [-0.82, 0.82]) {
    const hollow = new THREE.Mesh(new THREE.TorusGeometry(0.48, 0.14, 7, 14, Math.PI), barkLight);
    hollow.position.set(x, 0.78, r * 0.83);
    hollow.rotation.z = Math.PI;
    group.add(hollow);
  }
  for (let i = 0; i < 10; i++) {
    const a = (i / 10) * Math.PI * 2;
    const spike = new THREE.Mesh(new THREE.ConeGeometry(0.11, 0.78, 5), thorn);
    spike.position.set(Math.cos(a) * r * 0.92, 1.95, Math.sin(a) * r * 0.92);
    spike.rotation.z = Math.cos(a) * 0.65;
    spike.rotation.x = -Math.sin(a) * 0.65;
    group.add(spike);
  }
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
    const bloom = flower(petal, sap, 0.62);
    bloom.position.set(Math.cos(a) * 1.15, 4.35 + (i % 2) * 0.35, Math.sin(a) * 1.15);
    bloom.rotation.x = Math.PI / 2 - 0.35;
    group.add(bloom);
  }
  // Visible sap channels wind vertically up the tower.
  for (const sx of [-1, 1]) {
    const channel = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.09, 3.8, 6), sap);
    channel.position.set(sx * 0.46, 2.8, 0.72);
    channel.rotation.z = sx * 0.12;
    group.add(channel);
  }
  const canopy = new THREE.Mesh(new THREE.SphereGeometry(1.5, 9, 6), leaf);
  canopy.scale.y = 0.52;
  canopy.position.y = 5.05;
  group.add(canopy);

  const upgrade = new THREE.Group();
  upgrade.name = 'upgradeDeco';
  for (let i = 0; i < 7; i++) {
    const a = (i / 7) * Math.PI * 2;
    const root = new THREE.Mesh(new THREE.CylinderGeometry(0.18, 0.48, 2.2, 6), i % 2 ? sap : barkLight);
    root.position.set(Math.cos(a) * (r + 0.65), 0.45, Math.sin(a) * (r + 0.65));
    root.rotation.z = Math.cos(a) * 0.9;
    root.rotation.x = -Math.sin(a) * 0.9;
    upgrade.add(root);
  }
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2;
    const bloom = flower(leaf, sap, 0.82);
    bloom.position.set(Math.cos(a) * 1.72, 2.4, Math.sin(a) * 1.72);
    bloom.rotation.x = Math.PI / 2;
    upgrade.add(bloom);
  }
  upgrade.visible = false;
  group.add(upgrade);
  return finish(group);
}

function beastHollow(owner: 0 | 1, fp: Footprint): THREE.Group {
  const group = new THREE.Group();
  const earth = material(0x40372b, 1);
  const bark = material(mix(0x654b31, OWNER_COLORS[owner], 0.08), 1);
  const moss = material(0x55723b, 1);
  const bone = material(0xc5b88f, 0.9);
  const mushroom = luminous(0x9b6bb3, 0.75, 0.9);
  const vine = luminous(0x86bc56, 0.75, 0.84);
  const w = fp.w * 0.86;
  const mound = new THREE.Mesh(new THREE.SphereGeometry(w * 0.48, 10, 7), earth);
  mound.scale.set(1, 0.55, 0.8);
  mound.position.y = 1.1;
  group.add(mound);
  // Fallen-tree roof and ancient root arch.
  const log = new THREE.Mesh(new THREE.CylinderGeometry(0.72, 0.96, w, 9), bark);
  log.rotation.z = Math.PI / 2;
  log.position.set(0, 2.42, -0.35);
  group.add(log);
  for (const sx of [-1, 1]) {
    const root = new THREE.Mesh(new THREE.CylinderGeometry(0.22, 0.5, 3.8, 7), bark);
    root.position.set(sx * 1.82, 1.3, 0.5);
    root.rotation.z = sx * -0.82;
    group.add(root);
  }
  const mossCap = new THREE.Mesh(new THREE.SphereGeometry(2.2, 9, 6), moss);
  mossCap.scale.set(1.15, 0.24, 0.72);
  mossCap.position.set(0, 2.95, -0.25);
  group.add(mossCap);
  // Three roster-readable entrances.
  const xs = [-1.28, 0, 1.28];
  for (const x of xs) {
    const opening = new THREE.Mesh(new THREE.CircleGeometry(0.62, 10), material(0x171b15, 1));
    opening.position.set(x, 0.76, 1.72);
    group.add(opening);
  }
  // Razorhorn tracks.
  for (const x of [-1.48, -1.08]) {
    const track = new THREE.Mesh(new THREE.ConeGeometry(0.13, 0.36, 5), vine);
    track.position.set(x, 0.12, 2.28);
    track.rotation.x = Math.PI / 2;
    group.add(track);
  }
  // Mossboar tusk arch.
  for (const sx of [-1, 1]) {
    const tusk = new THREE.Mesh(new THREE.ConeGeometry(0.12, 0.95, 7), bone);
    tusk.position.set(sx * 0.43, 1.12, 1.82);
    tusk.rotation.z = sx * -0.72;
    group.add(tusk);
  }
  // Sporehide mushrooms.
  for (let i = 0; i < 5; i++) {
    const stem = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.09, 0.52, 6), bone);
    stem.position.set(0.82 + i * 0.2, 0.3 + (i % 2) * 0.1, 2.02 + (i % 2) * 0.22);
    group.add(stem);
    const cap = new THREE.Mesh(new THREE.SphereGeometry(0.2, 8, 5, 0, Math.PI * 2, 0, Math.PI / 2), mushroom);
    cap.position.set(stem.position.x, stem.position.y + 0.28, stem.position.z);
    group.add(cap);
  }
  const upgrade = new THREE.Group();
  upgrade.name = 'upgradeDeco';
  const antler = new THREE.Group();
  antler.position.y = 3.55;
  for (const sx of [-1, 1]) {
    const beam = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.12, 1.55, 6), bone);
    beam.position.set(sx * 0.48, 0, 0.5);
    beam.rotation.z = sx * 0.65;
    antler.add(beam);
    for (let i = 0; i < 3; i++) {
      const tine = new THREE.Mesh(new THREE.ConeGeometry(0.06, 0.62, 5), bone);
      tine.position.set(sx * (0.65 + i * 0.22), 0.35 + i * 0.22, 0.5);
      tine.rotation.z = sx * 0.75;
      antler.add(tine);
    }
  }
  upgrade.add(antler);
  for (let i = 0; i < 5; i++) {
    const trail = new THREE.Mesh(new THREE.TorusGeometry(1.55 + i * 0.28, 0.035, 5, 18, Math.PI * 0.55), vine);
    trail.rotation.x = Math.PI / 2;
    trail.rotation.z = i * 0.34;
    trail.position.y = 0.08;
    upgrade.add(trail);
  }
  upgrade.visible = false;
  group.add(upgrade);
  return finish(group);
}

function skybloomRoost(owner: 0 | 1, _fp: Footprint): THREE.Group {
  const group = new THREE.Group();
  const bark = material(mix(0x624630, OWNER_COLORS[owner], 0.08), 1);
  const leaf = material(0x548441, 0.96);
  const petal = material(0xd278a7, 0.84);
  const nectar = luminous(0xffd66d, 1.15, 0.78);
  // Offset trunk segments create a readable upward spiral.
  for (let i = 0; i < 7; i++) {
    const a = i * 0.72;
    const seg = new THREE.Mesh(new THREE.CylinderGeometry(0.42 - i * 0.025, 0.68 - i * 0.02, 1.35, 8), bark);
    seg.position.set(Math.cos(a) * 0.28, 0.66 + i * 0.92, Math.sin(a) * 0.28);
    seg.rotation.z = Math.cos(a) * 0.13;
    seg.rotation.x = Math.sin(a) * 0.13;
    group.add(seg);
    if (i > 1 && i % 2 === 0) {
      const platform = new THREE.Mesh(new THREE.SphereGeometry(1.05, 9, 6), leaf);
      platform.scale.set(1.35, 0.18, 0.72);
      platform.position.set(Math.cos(a) * 0.75, 0.8 + i * 0.92, Math.sin(a) * 0.75);
      platform.rotation.y = -a;
      group.add(platform);
    }
  }
  const launchFlower = flower(petal, nectar, 1.45);
  launchFlower.name = 'spinner';
  launchFlower.position.y = 7.15;
  group.add(launchFlower);
  const bud = new THREE.Mesh(new THREE.SphereGeometry(0.72, 9, 7), material(0x7da954, 0.9));
  bud.name = 'pulseCore';
  bud.scale.y = 1.35;
  bud.position.y = 6.7;
  group.add(bud);
  // Transparent nectar vines double as a visible thermal current.
  for (let i = 0; i < 3; i++) {
    const stream = new THREE.Mesh(new THREE.TorusGeometry(1.0 + i * 0.2, 0.055, 6, 28, Math.PI * 1.4), nectar);
    stream.rotation.x = Math.PI / 2;
    stream.rotation.z = i * 1.8;
    stream.position.y = 3.25 + i * 0.7;
    group.add(stream);
  }
  const upgrade = new THREE.Group();
  upgrade.name = 'upgradeDeco';
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2;
    const shieldLeaf = new THREE.Mesh(new THREE.SphereGeometry(0.95, 9, 6), leaf);
    shieldLeaf.scale.set(1.35, 0.18, 0.68);
    shieldLeaf.position.set(Math.cos(a) * 1.55, 6.3, Math.sin(a) * 1.55);
    shieldLeaf.rotation.y = -a;
    upgrade.add(shieldLeaf);
  }
  for (const sx of [-1, 1]) {
    const wingStream = new THREE.Mesh(new THREE.PlaneGeometry(2.4, 0.55), nectar);
    wingStream.position.set(sx * 1.38, 5.5, 0);
    wingStream.rotation.y = sx * -0.34;
    upgrade.add(wingStream);
  }
  upgrade.visible = false;
  group.add(upgrade);
  return finish(group);
}

function holoPanel(mat: THREE.Material, width: number, height: number): THREE.Mesh {
  return new THREE.Mesh(new THREE.PlaneGeometry(width, height), mat);
}

function precisionFoundry(owner: 0 | 1, fp: Footprint): THREE.Group {
  const group = new THREE.Group();
  const shell = material(mix(0xd9e2ef, OWNER_COLORS[owner], 0.12), 0.3, 0.56);
  const dark = material(0x28334a, 0.35, 0.62);
  const crystal = luminous(0x79d8ff, 1.25, 0.68);
  const violet = luminous(0x9b84ff, 1.05, 0.62);
  const w = fp.w * 0.9;
  const d = fp.h * 0.86;
  const base = new THREE.Mesh(new THREE.BoxGeometry(w, 0.35, d), shell);
  base.position.y = 0.3;
  group.add(base);
  // Straight rail with identical rifle frames.
  const rail = new THREE.Mesh(new THREE.BoxGeometry(0.32, 0.18, d * 0.9), dark);
  rail.position.y = 0.72;
  group.add(rail);
  const carriage = new THREE.Group();
  carriage.name = 'assemblyCarriage';
  for (let i = 0; i < 3; i++) {
    const frame = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.15, 1.2), shell);
    frame.position.set((i - 1) * 0.34, 1.05, 0);
    carriage.add(frame);
  }
  group.add(carriage);
  // Floating assembly rings and articulated prism arms.
  const spinner = new THREE.Group();
  spinner.name = 'spinner';
  spinner.position.y = 1.35;
  for (let i = 0; i < 4; i++) {
    const ring = new THREE.Mesh(new THREE.TorusGeometry(0.82 + i * 0.07, 0.055, 7, 18), i % 2 ? crystal : shell);
    ring.rotation.x = Math.PI / 2;
    ring.position.z = -1.45 + i * 0.95;
    spinner.add(ring);
  }
  group.add(spinner);
  for (const sx of [-1, 1]) {
    for (const z of [-1.35, 0, 1.35]) {
      const arm = new THREE.Mesh(new THREE.BoxGeometry(0.16, 1.45, 0.16), dark);
      arm.position.set(sx * 1.58, 1.22, z);
      arm.rotation.z = sx * 0.4;
      group.add(arm);
      const prism = new THREE.Mesh(new THREE.OctahedronGeometry(0.28, 0), violet);
      prism.position.set(sx * 1.22, 1.78, z);
      group.add(prism);
    }
  }
  // Four roof archives represented by transparent wireframe-like panels.
  const panelMat = new THREE.MeshBasicMaterial({ color: 0x78cfff, transparent: true, opacity: 0.35, side: THREE.DoubleSide, wireframe: true });
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2;
    const panel = holoPanel(panelMat, 0.95, 1.15);
    panel.position.set(Math.cos(a) * 1.75, 3.0, Math.sin(a) * 1.75);
    panel.rotation.y = -a;
    group.add(panel);
  }
  const upgrade = new THREE.Group();
  upgrade.name = 'upgradeDeco';
  const lens = new THREE.Mesh(new THREE.TorusGeometry(1.18, 0.12, 8, 24), crystal);
  lens.rotation.x = Math.PI / 2;
  lens.position.set(0, 4.1, d / 2 - 0.2);
  upgrade.add(lens);
  for (let i = 0; i < 5; i++) {
    const path = new THREE.Mesh(new THREE.BoxGeometry(0.035, 0.025, 2.5 + i * 0.45), violet);
    path.position.set((i - 2) * 0.26, 0.08, d / 2 + 1.15 + i * 0.18);
    path.rotation.y = (i - 2) * 0.08;
    upgrade.add(path);
  }
  upgrade.visible = false;
  group.add(upgrade);
  return finish(group);
}

function phaseCitadel(owner: 0 | 1, fp: Footprint): THREE.Group {
  const group = new THREE.Group();
  const shell = material(mix(0xcfdced, OWNER_COLORS[owner], 0.15), 0.3, 0.58);
  const dark = material(0x25304a, 0.32, 0.62);
  const star = luminous(0x756cff, 1.7, 0.78);
  const beam = luminous(0x73d9ff, 1.1, 0.5);
  const r = Math.min(fp.w, fp.h) * 0.42;
  // Angular defensive base with one aperture on every side.
  const base = new THREE.Mesh(new THREE.CylinderGeometry(r, r * 1.12, 1.35, 8), dark);
  base.position.y = 0.74;
  group.add(base);
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2;
    const aperture = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.34, 0.7, 8), beam);
    aperture.rotation.x = Math.PI / 2;
    aperture.rotation.z = -a;
    aperture.position.set(Math.cos(a) * r * 0.93, 0.9, Math.sin(a) * r * 0.93);
    group.add(aperture);
  }
  const core = new THREE.Mesh(new THREE.IcosahedronGeometry(0.82, 1), star);
  core.name = 'pulseCore';
  core.position.y = 3.0;
  group.add(core);
  const spinner = new THREE.Group();
  spinner.name = 'spinner';
  spinner.position.y = 3.0;
  for (let i = 0; i < 3; i++) {
    const ring = new THREE.Mesh(new THREE.TorusGeometry(1.2 + i * 0.45, 0.1 - i * 0.012, 7, 24), i % 2 ? shell : beam);
    ring.rotation.x = Math.PI / 2 + i * 0.48;
    ring.rotation.y = i * 0.62;
    spinner.add(ring);
  }
  group.add(spinner);
  const reverse = new THREE.Group();
  reverse.name = 'spinnerReverse';
  reverse.position.y = 3.0;
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2;
    const segment = new THREE.Mesh(new THREE.BoxGeometry(0.72, 0.32, 0.22), shell);
    segment.position.set(Math.cos(a) * 2.0, Math.sin(a * 2) * 0.28, Math.sin(a) * 2.0);
    segment.rotation.y = -a;
    reverse.add(segment);
  }
  group.add(reverse);
  // Constellation lines reach outward toward the network.
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2;
    const line = new THREE.Mesh(new THREE.BoxGeometry(0.035, 0.025, 3.0), beam);
    line.position.set(Math.cos(a) * (r + 1.1), 0.08, Math.sin(a) * (r + 1.1));
    line.rotation.y = -a;
    group.add(line);
  }
  const upgrade = new THREE.Group();
  upgrade.name = 'upgradeDeco';
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    const lock = new THREE.Mesh(new THREE.BoxGeometry(0.32, 0.7, 0.42), shell);
    lock.position.set(Math.cos(a) * 1.58, 3.0, Math.sin(a) * 1.58);
    lock.rotation.y = -a;
    upgrade.add(lock);
    const shield = new THREE.Mesh(new THREE.PlaneGeometry(1.2, 1.7), beam);
    shield.position.set(Math.cos(a) * 2.45, 2.15, Math.sin(a) * 2.45);
    shield.rotation.y = -a + Math.PI / 2;
    upgrade.add(shield);
  }
  upgrade.visible = false;
  group.add(upgrade);
  return finish(group);
}

function starforgeDock(owner: 0 | 1, fp: Footprint): THREE.Group {
  const group = new THREE.Group();
  group.position.y = 0.28;
  const shell = material(mix(0xd7e1ef, OWNER_COLORS[owner], 0.14), 0.3, 0.58);
  const dark = material(0x24304a, 0.32, 0.64);
  const star = luminous(0xffc85a, 1.85, 0.84);
  const blue = luminous(0x78cfff, 1.15, 0.68);
  const violet = luminous(0x9a7cff, 1.05, 0.62);
  const w = fp.w * 0.9;
  // Four hovering corner pads leave the center completely open.
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      const pad = new THREE.Mesh(new THREE.CylinderGeometry(0.72, 0.58, 0.28, 6), shell);
      pad.position.set(sx * w * 0.36, 0.28, sz * w * 0.36);
      group.add(pad);
      const thruster = new THREE.Mesh(new THREE.ConeGeometry(0.34, 0.72, 8), blue);
      thruster.position.set(sx * w * 0.36, -0.18, sz * w * 0.36);
      thruster.rotation.x = Math.PI;
      group.add(thruster);
    }
  }
  const core = new THREE.Mesh(new THREE.IcosahedronGeometry(0.78, 1), star);
  core.name = 'pulseCore';
  core.position.y = 2.15;
  group.add(core);
  const orbit = new THREE.Group();
  orbit.name = 'spinner';
  orbit.position.y = 2.15;
  const starRing = new THREE.Mesh(new THREE.TorusGeometry(1.25, 0.08, 7, 24), blue);
  starRing.rotation.x = Math.PI / 2;
  orbit.add(starRing);
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    const part = new THREE.Mesh(i % 2 ? new THREE.OctahedronGeometry(0.22, 0) : new THREE.BoxGeometry(0.28, 0.18, 0.42), i % 2 ? violet : shell);
    part.position.set(Math.cos(a) * 1.72, Math.sin(a * 3) * 0.32, Math.sin(a) * 1.72);
    part.rotation.y = -a;
    orbit.add(part);
  }
  group.add(orbit);
  // Orb construction frame on the left; sharp interceptor launch rails on the right.
  const frame = new THREE.Mesh(new THREE.TorusGeometry(1.42, 0.14, 8, 24), shell);
  frame.position.set(-1.65, 2.0, 0);
  frame.rotation.y = Math.PI / 2;
  group.add(frame);
  for (let i = -1; i <= 1; i++) {
    const rail = new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.16, 2.65), i === 0 ? violet : dark);
    rail.position.set(1.55 + i * 0.35, 1.0 + Math.abs(i) * 0.24, 0.3);
    rail.rotation.x = -0.16;
    group.add(rail);
    const arrow = new THREE.Mesh(new THREE.ConeGeometry(0.18, 0.65, 5), blue);
    arrow.rotation.x = Math.PI / 2;
    arrow.position.set(1.55 + i * 0.35, 1.18 + Math.abs(i) * 0.24, 1.75);
    group.add(arrow);
  }
  const upgrade = new THREE.Group();
  upgrade.name = 'upgradeDeco';
  const recovery = new THREE.Group();
  recovery.name = 'spinnerReverse';
  recovery.position.y = 2.15;
  for (let i = 0; i < 3; i++) {
    const ring = new THREE.Mesh(new THREE.TorusGeometry(2.15 + i * 0.34, 0.045, 6, 28), i % 2 ? violet : blue);
    ring.rotation.x = Math.PI / 2 + i * 0.18;
    recovery.add(ring);
  }
  for (let i = 0; i < 12; i++) {
    const a = (i / 12) * Math.PI * 2;
    const scrap = new THREE.Mesh(new THREE.TetrahedronGeometry(0.16 + (i % 3) * 0.05), shell);
    scrap.position.set(Math.cos(a) * 2.75, Math.sin(a * 4) * 0.55, Math.sin(a) * 2.75);
    recovery.add(scrap);
  }
  upgrade.add(recovery);
  upgrade.visible = false;
  group.add(upgrade);
  return finish(group);
}

// ---------------------------------------------------------------- Core Annex System structures

function brambleholdOutpost(owner: 0 | 1, fp: Footprint): THREE.Group {
  const group = new THREE.Group();
  const bark = material(mix(0x5f4429, OWNER_COLORS[owner], 0.1), 1);
  const bramble = material(0x4c3a24, 0.98);
  const thorn = material(0x50683a, 0.92);
  const petal = material(0xb75e78, 0.88);
  const heart = luminous(0x8fdc5f, 1.35, 0.92);
  const r = Math.min(fp.w, fp.h) * 0.44;
  const mound = new THREE.Mesh(new THREE.SphereGeometry(r * 1.15, 10, 7), material(0x4a3a29, 1));
  mound.scale.y = 0.22;
  mound.position.y = 0.1;
  group.add(mound);
  // woven bramble dome: a dark under-dome laced with interlocking arch canes
  const dome = new THREE.Mesh(new THREE.SphereGeometry(r * 0.92, 10, 7, 0, Math.PI * 2, 0, Math.PI / 2), bramble);
  dome.position.y = 0.22;
  group.add(dome);
  for (let i = 0; i < 6; i++) {
    const cane = new THREE.Mesh(new THREE.TorusGeometry(r * 0.96, 0.11, 6, 16, Math.PI), i % 2 ? bark : bramble);
    cane.rotation.y = (i / 6) * Math.PI;
    cane.position.y = 0.22;
    group.add(cane);
  }
  const upAxis = new THREE.Vector3(0, 1, 0);
  for (let i = 0; i < 9; i++) {
    const a = (i / 9) * Math.PI * 2;
    const el = 0.45 + (i % 3) * 0.35;
    const dir = new THREE.Vector3(Math.cos(a) * Math.cos(el), Math.sin(el), Math.sin(a) * Math.cos(el));
    const spikeMesh = new THREE.Mesh(new THREE.ConeGeometry(0.09, 0.62, 5), thorn);
    spikeMesh.position.copy(dir).multiplyScalar(r * 0.98).add(new THREE.Vector3(0, 0.22, 0));
    spikeMesh.quaternion.setFromUnitVectors(upAxis, dir);
    group.add(spikeMesh);
  }
  // crown opening framing the luminous green heart
  const rim = new THREE.Mesh(new THREE.TorusGeometry(0.55, 0.1, 6, 12), bark);
  rim.rotation.x = Math.PI / 2;
  rim.position.y = 0.22 + r * 0.9;
  group.add(rim);
  const core = new THREE.Mesh(new THREE.IcosahedronGeometry(0.42, 1), heart);
  core.name = 'pulseCore';
  core.position.y = 0.22 + r * 0.78;
  group.add(core);
  // small flowers at the foot of the weave
  for (const [fx, fz] of [[-r * 0.9, 0.75], [r * 0.95, -0.35], [-0.4, -r * 0.95]] as const) {
    const bloom = flower(petal, heart, 0.4);
    bloom.position.set(fx, 0.32, fz);
    group.add(bloom);
  }
  // door arch and the resource basket beside it (+z public front)
  const doorArch = new THREE.Mesh(new THREE.TorusGeometry(0.55, 0.12, 6, 12, Math.PI), bark);
  doorArch.position.set(0, 0.28, r * 0.82);
  group.add(doorArch);
  const doorway = new THREE.Mesh(new THREE.CircleGeometry(0.48, 10), material(0x171410, 1));
  doorway.position.set(0, 0.42, r * 0.86);
  group.add(doorway);
  const basket = new THREE.Mesh(new THREE.CylinderGeometry(0.42, 0.3, 0.5, 8), bark);
  basket.position.set(1.0, 0.25, r + 0.55);
  group.add(basket);
  for (let i = 0; i < 3; i++) {
    const berry = new THREE.Mesh(new THREE.SphereGeometry(0.14, 7, 5), heart);
    berry.position.set(0.85 + (i % 2) * 0.28, 0.56, r + 0.42 + Math.floor(i / 2) * 0.26);
    group.add(berry);
  }
  return finish(group);
}

function fieldBastion(owner: 0 | 1, fp: Footprint): THREE.Group {
  const group = new THREE.Group();
  const stone = material(mix(0xaba89e, OWNER_COLORS[owner], 0.08), 0.9);
  const iron = material(0x333943, 0.46, 0.62);
  const gold = material(0xc6a34f, 0.35, 0.7);
  const wood = material(0x6b4d33, 0.92);
  const w = fp.w * 0.84;
  const platform = new THREE.Mesh(new THREE.BoxGeometry(w, 0.3, w), stone);
  platform.position.y = 0.15;
  group.add(platform);
  // unfolded low walls, front gap on +z where the fort deploys troops
  const back = new THREE.Mesh(new THREE.BoxGeometry(w, 1.15, 0.35), wood);
  back.position.set(0, 0.85, -w / 2 + 0.18);
  group.add(back);
  for (const sx of [-1, 1]) {
    const side = new THREE.Mesh(new THREE.BoxGeometry(0.35, 1.15, w), wood);
    side.position.set(sx * (w / 2 - 0.18), 0.85, 0);
    group.add(side);
    const front = new THREE.Mesh(new THREE.BoxGeometry(w * 0.32, 1.15, 0.35), wood);
    front.position.set(sx * w * 0.33, 0.85, w / 2 - 0.18);
    group.add(front);
    // scaffolding: diagonal braces + a walk plank along each side wall
    for (const z of [-w * 0.3, w * 0.3]) {
      const brace = new THREE.Mesh(new THREE.BoxGeometry(0.09, 1.5, 0.09), wood);
      brace.position.set(sx * (w / 2 - 0.62), 0.72, z);
      brace.rotation.z = sx * 0.55;
      group.add(brace);
    }
    const plank = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.08, w * 0.82), wood);
    plank.position.set(sx * (w / 2 - 0.52), 1.32, 0);
    group.add(plank);
  }
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) {
    const post = new THREE.Mesh(new THREE.CylinderGeometry(0.14, 0.17, 2.3, 6), wood);
    post.position.set(sx * (w / 2 - 0.18), 1.15, sz * (w / 2 - 0.18));
    group.add(post);
    const cap = new THREE.Mesh(new THREE.ConeGeometry(0.22, 0.4, 6), iron);
    cap.position.set(sx * (w / 2 - 0.18), 2.45, sz * (w / 2 - 0.18));
    group.add(cap);
  }
  // central tower carrying the ballista (named `turret` inside the helper: tracks targets)
  const tower = new THREE.Mesh(new THREE.BoxGeometry(1.6, 2.3, 1.6), stone);
  tower.position.y = 1.45;
  group.add(tower);
  const deck = new THREE.Mesh(new THREE.BoxGeometry(2.1, 0.2, 2.1), wood);
  deck.position.y = 2.7;
  group.add(deck);
  const weapon = ballista(iron, gold);
  weapon.position.y = 3.0;
  group.add(weapon);
  const p = pole(2.2, gold);
  p.position.set(-w / 2 + 0.3, 3.4, -w / 2 + 0.3);
  group.add(p);
  const banner = flag(owner, 1.0, 0.62);
  banner.position.set(-w / 2 + 0.8, 4.15, -w / 2 + 0.3);
  group.add(banner);
  return finish(group);
}

function bloodCocoon(_owner: 0 | 1, fp: Footprint): THREE.Group {
  const group = new THREE.Group();
  const flesh = material(0x5f2b30, 0.9);
  const char = material(0x2a2226, 0.75, 0.3);
  const blood = luminous(0xc21f31, 1.2, 0.62);
  const base = new THREE.Mesh(new THREE.CylinderGeometry(fp.w * 0.4, fp.w * 0.48, 0.5, 10), flesh);
  base.position.y = 0.25;
  group.add(base);
  // pulsing red membrane, mostly wrapped by the flesh bands
  const membrane = new THREE.Mesh(new THREE.CapsuleGeometry(0.95, 2.3, 6, 10), blood);
  membrane.name = 'pulseCore';
  membrane.position.y = 2.45;
  group.add(membrane);
  for (let i = 0; i < 7; i++) {
    const bulge = Math.sin(((i + 0.5) / 7) * Math.PI);
    const band = new THREE.Mesh(new THREE.TorusGeometry(0.55 + bulge * 0.62, 0.22, 6, 14), i % 2 ? char : flesh);
    band.rotation.x = Math.PI / 2;
    band.position.y = 0.75 + i * 0.62;
    group.add(band);
  }
  const tip = new THREE.Mesh(new THREE.ConeGeometry(0.42, 1.2, 7), flesh);
  tip.position.set(0.18, 5.25, 0);
  tip.rotation.z = -0.3;
  group.add(tip);
  // collapsed iron cage fragments left over from the crucible arc
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * Math.PI * 2 + 0.4;
    const bar = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 0.9 + (i % 2) * 0.3, 4), char);
    bar.position.set(Math.cos(a) * 1.25, 0.3, Math.sin(a) * 1.25);
    bar.rotation.z = Math.cos(a) * 1.1;
    bar.rotation.x = -Math.sin(a) * 1.1;
    group.add(bar);
  }
  const brokenRing = new THREE.Mesh(new THREE.TorusGeometry(0.55, 0.06, 5, 12, Math.PI * 0.7), char);
  brokenRing.rotation.x = Math.PI / 2 - 0.25;
  brokenRing.position.set(0.95, 0.14, 0.85);
  group.add(brokenRing);
  return finish(group);
}

function graveBeacon(_owner: 0 | 1, fp: Footprint): THREE.Group {
  const group = new THREE.Group();
  const stone = material(0x565049, 0.92);
  const char = material(0x2b2529, 0.75, 0.3);
  const bone = material(0xb0a48c, 0.92);
  const soulGreen = luminous(0x7fd44a, 1.5, 0.85);
  // crooked tower: stacked courses leaning further with height
  const heights = [1.5, 1.25, 1.05, 0.9];
  let y = 0;
  for (let i = 0; i < heights.length; i++) {
    const seg = new THREE.Mesh(new THREE.BoxGeometry(1.9 - i * 0.28, heights[i], 1.9 - i * 0.28), i % 2 ? char : stone);
    y += heights[i] / 2;
    seg.position.set(i * 0.22, y, -i * 0.08);
    seg.rotation.z = -(i + 1) * 0.055;
    group.add(seg);
    y += heights[i] / 2;
  }
  // open belfry with a tilted cap and the hanging funeral bell
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) {
    const post = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.09, 0.95, 5), char);
    post.position.set(0.66 + sx * 0.5, y + 0.48, -0.24 + sz * 0.5);
    group.add(post);
  }
  const cap = new THREE.Mesh(new THREE.ConeGeometry(1.05, 0.75, 4), char);
  cap.position.set(0.74, y + 1.1, -0.24);
  cap.rotation.z = -0.22;
  cap.rotation.y = Math.PI / 4;
  group.add(cap);
  const bell = new THREE.Group();
  bell.name = 'pump';
  bell.position.set(0.66, y + 0.45, -0.24);
  const hanger = pole(0.35, char);
  hanger.position.y = 0.32;
  bell.add(hanger);
  const bellBody = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.36, 0.45, 9), char);
  bell.add(bellBody);
  const clapper = new THREE.Mesh(new THREE.SphereGeometry(0.06, 6, 5), bone);
  clapper.position.y = -0.29;
  bell.add(clapper);
  group.add(bell);
  const glow = new THREE.Mesh(new THREE.OctahedronGeometry(0.4, 0), soulGreen);
  glow.name = 'pulseCore';
  glow.position.set(0.8, y + 1.85, -0.24);
  group.add(glow);
  // skeletal arms clawing out of the ground around the base
  const r = fp.w * 0.42;
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2 + 0.7;
    const ax = Math.cos(a) * r;
    const az = Math.sin(a) * r;
    const forearm = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.1, 1.1, 5), bone);
    forearm.position.set(ax, 0.5, az);
    forearm.rotation.z = Math.cos(a) * 0.45;
    forearm.rotation.x = -Math.sin(a) * 0.45;
    group.add(forearm);
    for (let f = -1; f <= 1; f++) {
      const finger = new THREE.Mesh(new THREE.ConeGeometry(0.035, 0.32, 4), bone);
      finger.position.set(ax + Math.cos(a) * 0.28 + f * 0.11, 1.08, az + Math.sin(a) * 0.28 + f * 0.07);
      finger.rotation.z = Math.cos(a) * 0.5 + f * 0.28;
      group.add(finger);
    }
  }
  for (const [hx, hz, hr] of [[-1.15, 0.9, 0.28], [1.05, 1.2, -0.2]] as const) {
    const headstone = new THREE.Mesh(new THREE.BoxGeometry(0.42, 0.72, 0.16), stone);
    headstone.position.set(hx, 0.34, hz);
    headstone.rotation.z = hr;
    group.add(headstone);
  }
  return finish(group);
}

function astralRelay(owner: 0 | 1, _fp: Footprint): THREE.Group {
  const group = new THREE.Group();
  const shell = material(mix(0xd7e1ef, OWNER_COLORS[owner], 0.14), 0.3, 0.6);
  const dark = material(0x223050, 0.34, 0.66);
  const blue = luminous(0x6fd2ff, 1.3, 0.85);
  // airy tripod feet — the pylon barely touches the ground
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2 + Math.PI / 6;
    const leg = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.1, 1.7, 5), dark);
    leg.position.set(Math.cos(a) * 0.62, 0.8, Math.sin(a) * 0.62);
    leg.rotation.z = Math.cos(a) * 0.42;
    leg.rotation.x = -Math.sin(a) * 0.42;
    group.add(leg);
  }
  const pylon = new THREE.Mesh(new THREE.CylinderGeometry(0.14, 0.24, 3.1, 6), shell);
  pylon.position.y = 3.0;
  group.add(pylon);
  for (const sx of [-1, 1]) {
    const seam = new THREE.Mesh(new THREE.BoxGeometry(0.045, 2.6, 0.045), blue);
    seam.position.set(sx * 0.15, 3.0, 0);
    group.add(seam);
  }
  const crystal = new THREE.Mesh(new THREE.OctahedronGeometry(0.55, 0), blue);
  crystal.name = 'orb';
  crystal.position.y = 5.35;
  group.add(crystal);
  const ringGroup = new THREE.Group();
  ringGroup.name = 'spinner';
  ringGroup.position.y = 5.35;
  const ring = new THREE.Mesh(new THREE.TorusGeometry(0.95, 0.05, 6, 20), shell);
  ring.rotation.x = Math.PI / 2;
  ringGroup.add(ring);
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2;
    const shard = new THREE.Mesh(new THREE.OctahedronGeometry(0.14, 0), i % 2 ? dark : blue);
    shard.position.set(Math.cos(a) * 0.95, 0, Math.sin(a) * 0.95);
    ringGroup.add(shard);
  }
  group.add(ringGroup);
  return finish(group);
}

// ---------------------------------------------------------------- Core Annex System sockets
// Each faction Core carries three socketed annex models as named `annex-<upgradeId>` groups.
// They start invisible; the renderer reveals (and grows) them from `b.upgradesDone` /
// `b.upgradeInProgress`. Socket convention: top = crowning the core, left = -x at ground,
// bottom = +z public front at ground.

function ironCoreAnnexes(
  owner: 0 | 1,
  stone: THREE.Material,
  iron: THREE.Material,
  gold: THREE.Material,
  power: THREE.Material,
): THREE.Group[] {
  // top socket — High Command Hall: signal tower with a lit brazier, war banners, drum deck.
  const hall = new THREE.Group();
  hall.name = 'annex-up-ic-high-command-hall';
  const deck = new THREE.Mesh(new THREE.CylinderGeometry(2.5, 2.15, 0.4, 10), stone);
  deck.position.y = 9.3;
  hall.add(deck);
  for (const sx of [-1, 1]) {
    const drum = new THREE.Mesh(new THREE.CylinderGeometry(0.42, 0.42, 0.5, 9), gold);
    drum.position.set(sx * 1.55, 9.75, 1.15);
    hall.add(drum);
  }
  const spire = new THREE.Mesh(new THREE.CylinderGeometry(1.0, 1.35, 3.6, 8), stone);
  spire.position.y = 11.3;
  hall.add(spire);
  for (const sx of [-1, 1]) {
    const p = pole(2.0, gold);
    p.position.set(sx * 1.45, 12.4, 0);
    hall.add(p);
    const banner = flag(owner, 0.95, 0.6);
    banner.position.set(sx * 1.45 + 0.48, 13.1, 0);
    hall.add(banner);
  }
  const drape = flag(owner, 0.85, 2.3);
  drape.position.set(0, 11.5, 1.12);
  hall.add(drape);
  const bowl = new THREE.Mesh(new THREE.CylinderGeometry(0.8, 0.5, 0.5, 8), iron);
  bowl.position.y = 13.35;
  hall.add(bowl);
  const fire = new THREE.Mesh(new THREE.OctahedronGeometry(0.55, 0), power);
  fire.name = 'pulseCore';
  fire.position.y = 13.9;
  hall.add(fire);

  // left socket — Royal Works: stone workshop, big rotating crane, molten glow channel.
  const works = new THREE.Group();
  works.name = 'annex-up-ic-royal-works';
  const shop = new THREE.Mesh(new THREE.BoxGeometry(2.5, 2.2, 2.8), stone);
  shop.position.set(-4.35, 1.1, -0.55);
  works.add(shop);
  const shopRoof = new THREE.Mesh(new THREE.BoxGeometry(2.9, 0.28, 3.2), iron);
  shopRoof.position.set(-4.35, 2.34, -0.55);
  works.add(shopRoof);
  const stack = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.28, 1.6, 6), iron);
  stack.position.set(-5.1, 3.1, -1.3);
  works.add(stack);
  const channel = new THREE.Mesh(new THREE.BoxGeometry(2.3, 0.09, 0.42), power);
  channel.position.set(-3.45, 0.1, 1.2);
  works.add(channel);
  const crucible = new THREE.Mesh(new THREE.CylinderGeometry(0.44, 0.3, 0.45, 8), iron);
  crucible.position.set(-4.75, 0.25, 1.2);
  works.add(crucible);
  const melt = new THREE.Mesh(new THREE.CylinderGeometry(0.31, 0.31, 0.1, 8), power);
  melt.position.set(-4.75, 0.5, 1.2);
  works.add(melt);
  for (let i = 0; i < 4; i++) {
    const block = new THREE.Mesh(new THREE.BoxGeometry(0.62, 0.5, 0.62), stone);
    block.position.set(-3.6 + (i % 2) * 0.68, 0.27 + Math.floor(i / 2) * 0.52, 2.3);
    block.rotation.y = i * 0.35;
    works.add(block);
  }
  const mast = new THREE.Mesh(new THREE.CylinderGeometry(0.13, 0.2, 6.0, 7), iron);
  mast.position.set(-5.35, 3.0, 1.8);
  works.add(mast);
  const crane = new THREE.Group();
  crane.name = 'spinner';
  crane.position.set(-5.35, 6.0, 1.8);
  const jib = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.18, 3.6), iron);
  jib.position.z = 0.9;
  crane.add(jib);
  const counter = new THREE.Mesh(new THREE.BoxGeometry(0.52, 0.36, 0.62), stone);
  counter.position.z = -1.1;
  crane.add(counter);
  const cable = pole(1.2, iron);
  cable.position.set(0, -0.6, 1.85);
  crane.add(cable);
  const load = new THREE.Mesh(new THREE.BoxGeometry(0.6, 0.5, 0.6), stone);
  load.position.set(0, -1.5, 1.85);
  crane.add(load);
  const finial = new THREE.Mesh(new THREE.ConeGeometry(0.16, 0.4, 6), gold);
  finial.position.y = 0.3;
  crane.add(finial);
  works.add(crane);

  // bottom socket — Marching Foundry: low industrial hall, smokestack, outbound rail line.
  const foundry = new THREE.Group();
  foundry.name = 'annex-up-ic-marching-foundry';
  const hallShell = new THREE.Mesh(new THREE.BoxGeometry(3.4, 1.7, 2.2), stone);
  hallShell.position.set(-0.9, 0.85, 4.2);
  foundry.add(hallShell);
  const hallRoof = new THREE.Mesh(new THREE.BoxGeometry(3.7, 0.24, 2.5), iron);
  hallRoof.position.set(-0.9, 1.8, 4.2);
  foundry.add(hallRoof);
  const mouth = new THREE.Mesh(new THREE.BoxGeometry(0.95, 0.75, 0.1), power);
  mouth.position.set(-0.9, 0.7, 5.32);
  foundry.add(mouth);
  const chimney = new THREE.Mesh(new THREE.CylinderGeometry(0.3, 0.42, 3.1, 7), iron);
  chimney.position.set(-2.1, 2.55, 3.7);
  foundry.add(chimney);
  for (const x of [1.35, 2.05]) {
    const rail = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.1, 4.2), iron);
    rail.position.set(x, 0.14, 5.3);
    foundry.add(rail);
  }
  for (let i = 0; i < 6; i++) {
    const sleeper = new THREE.Mesh(new THREE.BoxGeometry(1.3, 0.06, 0.24), stone);
    sleeper.position.set(1.7, 0.08, 3.5 + i * 0.75);
    foundry.add(sleeper);
  }
  const cart = new THREE.Mesh(new THREE.BoxGeometry(1.0, 0.5, 1.3), iron);
  cart.position.set(1.7, 0.55, 4.35);
  foundry.add(cart);
  for (const x of [1.35, 2.05]) for (const dz of [-0.42, 0.42]) {
    const wheel = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.2, 0.1, 8), stone);
    wheel.rotation.z = Math.PI / 2;
    wheel.position.set(x, 0.24, 4.35 + dz);
    foundry.add(wheel);
  }
  for (let i = 0; i < 3; i++) {
    const shell = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.15, 0.55, 6), gold);
    shell.position.set(1.45 + i * 0.26, 0.98, 4.3 - (i % 2) * 0.3);
    foundry.add(shell);
  }

  for (const annex of [hall, works, foundry]) annex.visible = false;
  return [hall, works, foundry];
}

function remainingIronBuilding(defId: string, owner: 0 | 1, fp: Footprint): THREE.Group {
  const group = new THREE.Group();
  const stone = material(mix(0xaaa79f, OWNER_COLORS[owner], 0.08), 0.88);
  const iron = material(mix(0x333943, OWNER_COLORS[owner], 0.12), 0.45, 0.65);
  const gold = material(0xc6a04c, 0.34, 0.72);
  const power = luminous(0xffbd42, 1.3);
  const wall = defId === 'b-ic-castle-wall';
  const standard = defId === 'b-ic-ritual-standard';
  const core = defId === 'b-ic-crown-bastion';
  const w = wall ? fp.w + 0.8 : Math.max(1.2, fp.w * 0.8);
  const d = wall ? fp.h : Math.max(1.2, fp.h * 0.78);

  if (standard) {
    const plinth = new THREE.Mesh(new THREE.CylinderGeometry(0.9, 1.15, 0.45, 8), iron);
    plinth.position.y = 0.22;
    group.add(plinth);
    const mast = pole(5.2, gold);
    mast.position.y = 2.8;
    group.add(mast);
    for (let i = 0; i < 3; i++) {
      const banner = flag(owner, 1.15 - i * 0.14, 0.62);
      banner.position.set(0.58, 4.65 - i * 0.85, 0);
      group.add(banner);
    }
    const crown = new THREE.Mesh(new THREE.OctahedronGeometry(0.34, 0), power);
    crown.name = 'pulseCore';
    crown.position.y = 5.55;
    group.add(crown);
    return finish(group);
  }

  if (wall) {
    const curtain = new THREE.Mesh(new THREE.BoxGeometry(w, 3.8, d), stone);
    curtain.position.y = 1.9;
    group.add(curtain);
    const walk = new THREE.Mesh(new THREE.BoxGeometry(w + 0.5, 0.35, d), iron);
    walk.position.y = 3.95;
    group.add(walk);
    for (let z = -d / 2 + 0.5; z <= d / 2 - 0.5; z += 1.15) {
      const merlon = new THREE.Mesh(new THREE.BoxGeometry(w + 0.28, 0.58, 0.48), stone);
      merlon.position.set(0, 4.42, z);
      group.add(merlon);
    }
    for (const z of [-d / 2, d / 2]) {
      const tower = new THREE.Mesh(new THREE.CylinderGeometry(1.05, 1.28, 5.4, 8), iron);
      tower.position.set(0, 2.7, z);
      group.add(tower);
      const cap = new THREE.Mesh(new THREE.ConeGeometry(1.2, 0.75, 8), gold);
      cap.position.set(0, 5.75, z);
      group.add(cap);
    }
    return finish(group);
  }

  const base = new THREE.Mesh(new THREE.BoxGeometry(w, core ? 5.2 : 2.8, d), stone);
  base.position.y = core ? 2.6 : 1.4;
  group.add(base);
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) {
    const buttress = new THREE.Mesh(new THREE.BoxGeometry(0.5, core ? 6.2 : 3.4, 0.5), iron);
    buttress.position.set(sx * (w / 2 - 0.15), core ? 3.1 : 1.7, sz * (d / 2 - 0.15));
    group.add(buttress);
  }
  crenels(group, w, d, core ? 5.5 : 3.15, stone);
  if (core) {
    const keep = new THREE.Mesh(new THREE.CylinderGeometry(1.7, 2.2, 5.2, 8), iron);
    keep.position.y = 6.5;
    group.add(keep);
    const crown = new THREE.Mesh(new THREE.TorusGeometry(2.1, 0.18, 7, 18), gold);
    crown.name = 'spinner';
    crown.rotation.x = Math.PI / 2;
    crown.position.y = 8.8;
    group.add(crown);
    const coreGem = new THREE.Mesh(new THREE.OctahedronGeometry(0.72, 0), power);
    coreGem.name = 'pulseCore';
    coreGem.position.y = 8.8;
    group.add(coreGem);
    for (const annex of ironCoreAnnexes(owner, stone, iron, gold, power)) group.add(annex);
  } else if (defId === 'b-ic-builder-guild') {
    const carriage = new THREE.Group();
    carriage.name = 'assemblyCarriage';
    carriage.position.set(0, 2.0, 0);
    const beam = new THREE.Mesh(new THREE.BoxGeometry(w + 0.8, 0.18, 0.22), gold);
    carriage.add(beam);
    const hook = new THREE.Mesh(new THREE.TorusGeometry(0.28, 0.07, 6, 10, Math.PI * 1.5), iron);
    hook.position.y = -0.7;
    carriage.add(hook);
    group.add(carriage);
  } else if (defId === 'b-ic-bulwark-arbalest') {
    const weapon = ballista(iron, gold);
    weapon.position.y = 4.0;
    weapon.scale.setScalar(1.2);
    group.add(weapon);
  } else {
    const port = new THREE.Mesh(new THREE.TorusGeometry(1.1, 0.22, 7, 14, Math.PI), gold);
    port.position.set(0, 1.45, d / 2 + 0.12);
    group.add(port);
  }
  return finish(group);
}

function ashenCoreAnnexes(
  bone: THREE.Material,
  char: THREE.Material,
  flesh: THREE.Material,
  soul: THREE.Material,
): THREE.Group[] {
  // top socket — Sanguine Crucible: ribbed altar spire with five hanging soul cages. The
  // inner orbs (`soulCage0..4`) start invisible; the renderer lights one per banked soul.
  const crucible = new THREE.Group();
  crucible.name = 'annex-up-ah-sanguine-crucible';
  const dish = new THREE.Mesh(new THREE.CylinderGeometry(1.4, 1.0, 0.4, 10), bone);
  dish.position.y = 6.3;
  crucible.add(dish);
  const pool = new THREE.Mesh(new THREE.CylinderGeometry(1.05, 1.05, 0.12, 10), soul);
  pool.position.y = 6.55;
  crucible.add(pool);
  const spike = new THREE.Mesh(new THREE.CylinderGeometry(0.32, 0.55, 3.6, 7), bone);
  spike.position.y = 8.2;
  crucible.add(spike);
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
    const rib = boneRib(char, 0.72, 2.4);
    rib.position.set(Math.cos(a) * 0.78, 8.0, Math.sin(a) * 0.78);
    rib.rotation.y = -a;
    crucible.add(rib);
  }
  const crownOrb = new THREE.Mesh(new THREE.OctahedronGeometry(0.4, 0), soul);
  crownOrb.position.y = 10.35;
  crucible.add(crownOrb);
  for (let i = 0; i < 5; i++) {
    const arm = new THREE.Group();
    arm.rotation.y = (i - 2) * 0.55; // arc of cages fanned toward the public front (+z)
    crucible.add(arm);
    const beam = new THREE.Mesh(new THREE.CylinderGeometry(0.055, 0.055, 1.7, 5), bone);
    beam.rotation.x = Math.PI / 2;
    beam.position.set(0, 9.3, 1.2);
    arm.add(beam);
    const chain = pole(0.6, char);
    chain.position.set(0, 8.95, 2.0);
    arm.add(chain);
    const cage = new THREE.Group();
    cage.position.set(0, 8.35, 2.0);
    for (let bar = 0; bar < 4; bar++) {
      const ba = (bar / 4) * Math.PI * 2;
      const rod = new THREE.Mesh(new THREE.CylinderGeometry(0.028, 0.028, 0.6, 4), char);
      rod.position.set(Math.cos(ba) * 0.22, 0, Math.sin(ba) * 0.22);
      cage.add(rod);
    }
    for (const cy of [-0.3, 0.3]) {
      const ring = new THREE.Mesh(new THREE.TorusGeometry(0.22, 0.035, 5, 10), char);
      ring.rotation.x = Math.PI / 2;
      ring.position.y = cy;
      cage.add(ring);
    }
    const orb = new THREE.Mesh(new THREE.SphereGeometry(0.16, 8, 6), soul);
    orb.name = `soulCage${i}`;
    orb.visible = false;
    cage.add(orb);
    arm.add(cage);
  }

  // left socket — Ossuary Ledger: ribcage archive over stacked bone tablets with a lectern.
  const ledger = new THREE.Group();
  ledger.name = 'annex-up-ah-ossuary-ledger';
  for (let i = 0; i < 5; i++) {
    const tablet = new THREE.Mesh(new THREE.BoxGeometry(1.6, 0.2, 1.0), i % 2 ? bone : char);
    tablet.position.set(-4.2, 0.32 + i * 0.24, -0.2);
    tablet.rotation.y = (i % 2 ? 0.12 : -0.09);
    ledger.add(tablet);
  }
  for (let i = 0; i < 4; i++) {
    const arch = new THREE.Mesh(new THREE.TorusGeometry(0.95, 0.09, 6, 12, Math.PI), bone);
    arch.scale.y = 1.5;
    arch.position.set(-4.2, 0.85, -1.35 + i * 0.85);
    ledger.add(arch);
  }
  const lecternPost = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.26, 0.95, 6), char);
  lecternPost.position.set(-4.1, 0.48, 1.75);
  ledger.add(lecternPost);
  const lecternTop = new THREE.Mesh(new THREE.BoxGeometry(0.72, 0.1, 0.52), flesh);
  lecternTop.position.set(-4.1, 1.02, 1.75);
  lecternTop.rotation.x = -0.5;
  ledger.add(lecternTop);
  const tome = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.07, 0.36), bone);
  tome.position.set(-4.1, 1.12, 1.72);
  tome.rotation.x = -0.5;
  ledger.add(tome);
  const shackle = new THREE.Mesh(new THREE.TorusGeometry(0.3, 0.04, 5, 10, Math.PI * 1.3), char);
  shackle.position.set(-3.75, 0.62, 1.55);
  shackle.rotation.z = 1.2;
  ledger.add(shackle);
  for (const dz of [-0.35, 0.3]) {
    const candle = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.06, 0.3, 5), soul);
    candle.position.set(-4.25, 1.6, -0.2 + dz);
    ledger.add(candle);
  }

  // bottom socket — Carrion Procession: black carriage stable + hanging funeral bell.
  const procession = new THREE.Group();
  procession.name = 'annex-up-ah-carrion-procession';
  const stable = new THREE.Mesh(new THREE.BoxGeometry(3.2, 1.9, 2.1), char);
  stable.position.set(1.05, 0.95, 4.3);
  procession.add(stable);
  const stableRoof = new THREE.Mesh(new THREE.BoxGeometry(3.5, 0.22, 2.4), flesh);
  stableRoof.position.set(1.05, 1.95, 4.3);
  procession.add(stableRoof);
  const ridge = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.09, 3.4, 5), bone);
  ridge.rotation.z = Math.PI / 2;
  ridge.position.set(1.05, 2.1, 4.3);
  procession.add(ridge);
  const doorway = new THREE.Mesh(new THREE.BoxGeometry(2.5, 1.35, 0.08), material(0x120e12, 1));
  doorway.position.set(1.05, 0.72, 5.37);
  procession.add(doorway);
  const coach = new THREE.Mesh(new THREE.BoxGeometry(1.15, 0.95, 1.9), char);
  coach.position.set(-1.85, 1.0, 4.4);
  procession.add(coach);
  const coachCrest = new THREE.Mesh(new THREE.BoxGeometry(1.25, 0.14, 2.0), bone);
  coachCrest.position.set(-1.85, 1.52, 4.4);
  procession.add(coachCrest);
  for (const sx of [-1, 1]) for (const dz of [-0.6, 0.6]) {
    const wheel = new THREE.Mesh(new THREE.CylinderGeometry(0.45, 0.45, 0.1, 10), flesh);
    wheel.rotation.z = Math.PI / 2;
    wheel.position.set(-1.85 + sx * 0.64, 0.45, 4.4 + dz);
    procession.add(wheel);
  }
  const lantern = new THREE.Mesh(new THREE.SphereGeometry(0.1, 6, 5), soul);
  lantern.position.set(-1.85, 1.28, 5.42);
  procession.add(lantern);
  for (const px of [-1.15, 0.45]) {
    const post = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.12, 2.9, 6), char);
    post.position.set(px, 1.45, 5.75);
    procession.add(post);
  }
  const crossbeam = new THREE.Mesh(new THREE.BoxGeometry(1.9, 0.16, 0.16), bone);
  crossbeam.position.set(-0.35, 2.85, 5.75);
  procession.add(crossbeam);
  const bell = new THREE.Group();
  bell.name = 'pump';
  bell.position.set(-0.35, 2.2, 5.75);
  const hanger = pole(0.4, char);
  hanger.position.y = 0.38;
  bell.add(hanger);
  const bellBody = new THREE.Mesh(new THREE.CylinderGeometry(0.24, 0.42, 0.55, 9), char);
  bell.add(bellBody);
  const clapper = new THREE.Mesh(new THREE.SphereGeometry(0.07, 6, 5), bone);
  clapper.position.y = -0.34;
  bell.add(clapper);
  procession.add(bell);

  for (const annex of [crucible, ledger, procession]) annex.visible = false;
  return [crucible, ledger, procession];
}

function remainingAshenBuilding(defId: string, owner: 0 | 1, fp: Footprint): THREE.Group {
  const group = new THREE.Group();
  const bone = material(mix(0xa79b86, OWNER_COLORS[owner], 0.07), 0.96);
  const char = material(0x292329, 0.72, 0.34);
  const flesh = material(0x67433f, 0.98);
  const soul = luminous(0xc51432, 1.55);
  const wall = defId === 'b-ah-castle-wall';
  const standard = defId === 'b-ah-ritual-standard';
  const core = defId === 'b-ah-ossuary-throne';
  const w = wall ? fp.w + 0.6 : Math.max(1.2, fp.w * 0.78);
  const d = wall ? fp.h : Math.max(1.2, fp.h * 0.78);

  if (standard) {
    const mound = new THREE.Mesh(new THREE.SphereGeometry(0.95, 8, 6), flesh);
    mound.scale.y = 0.42;
    mound.position.y = 0.35;
    group.add(mound);
    const spine = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.24, 4.7, 7), bone);
    spine.position.y = 2.6;
    group.add(spine);
    for (let i = 0; i < 5; i++) {
      const rib = new THREE.Mesh(new THREE.TorusGeometry(0.65 - i * 0.07, 0.055, 6, 11, Math.PI), bone);
      rib.position.set(0, 3.8 - i * 0.52, 0);
      rib.rotation.y = Math.PI / 2;
      group.add(rib);
    }
    const flame = new THREE.Mesh(new THREE.OctahedronGeometry(0.35, 0), soul);
    flame.name = 'pulseCore';
    flame.position.y = 5.15;
    group.add(flame);
    return finish(group);
  }

  const mound = new THREE.Mesh(new THREE.BoxGeometry(w, wall ? 3.1 : core ? 4.4 : 2.4, d), flesh);
  mound.position.y = wall ? 1.55 : core ? 2.2 : 1.2;
  mound.scale.x = 0.92;
  group.add(mound);
  const ribCount = wall ? Math.max(8, Math.round(d / 0.85)) : core ? 11 : 7;
  for (let i = 0; i < ribCount; i++) {
    const z = wall ? -d / 2 + 0.45 + i * ((d - 0.9) / Math.max(1, ribCount - 1)) : -d / 2 + 0.35 + i * ((d - 0.7) / Math.max(1, ribCount - 1));
    const rib = new THREE.Mesh(new THREE.TorusGeometry(w * (wall ? 0.48 : 0.54), 0.075, 6, 12, Math.PI), bone);
    rib.position.set(0, wall ? 2.0 : core ? 3.25 : 1.85, z);
    rib.rotation.x = Math.PI / 2;
    rib.rotation.z = Math.PI;
    group.add(rib);
  }
  for (const z of [-d / 2, d / 2]) {
    const spine = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.34, wall ? 4.9 : core ? 7 : 3.8, 7), bone);
    spine.position.set(0, wall ? 2.45 : core ? 3.5 : 1.9, z);
    group.add(spine);
  }
  const coreGem = new THREE.Mesh(new THREE.IcosahedronGeometry(core ? 0.9 : 0.48, 1), soul);
  coreGem.name = 'pulseCore';
  coreGem.position.set(0, core ? 5.3 : wall ? 2.5 : 2.4, 0);
  group.add(coreGem);
  if (defId === 'b-ah-soul-mill' || core) {
    const cage = new THREE.Group();
    cage.name = 'spinner';
    cage.position.copy(coreGem.position);
    for (let i = 0; i < 3; i++) {
      const ring = new THREE.Mesh(new THREE.TorusGeometry((core ? 1.45 : 0.9) + i * 0.18, 0.055, 6, 18), i % 2 ? char : bone);
      ring.rotation.set(Math.PI / 2 + i * 0.35, i * 0.6, 0);
      cage.add(ring);
    }
    group.add(cage);
  }
  if (core) {
    for (const annex of ashenCoreAnnexes(bone, char, flesh, soul)) group.add(annex);
  }
  if (defId === 'b-ah-devouring-gate') {
    for (const sx of [-1, 1]) {
      for (let i = 0; i < 5; i++) {
        const tooth = new THREE.Mesh(new THREE.ConeGeometry(0.16, 0.8, 5), bone);
        tooth.position.set(sx * (0.55 + i * 0.13), 1.2 + i * 0.35, d / 2 + 0.35);
        tooth.rotation.z = sx * -0.65;
        group.add(tooth);
      }
    }
  }
  return finish(group);
}

function remainingVerdantBuilding(defId: string, owner: 0 | 1, fp: Footprint): THREE.Group {
  const group = new THREE.Group();
  const bark = material(mix(0x65492f, OWNER_COLORS[owner], 0.08), 1);
  const barkLight = material(0x86643e, 0.98);
  const leaf = material(0x547e3b, 1);
  const bloom = luminous(0x8fda65, 1.15);
  const standard = defId === 'b-vw-ritual-standard';
  const sapling = defId === 'b-vw-sapling';
  const defense = defId === 'b-vw-defense-grove' || defId === 'b-vw-briarwatch-tower';
  const scale = sapling ? 0.52 : standard ? 0.72 : 1;
  const trunkH = (defense ? 5.2 : standard ? 4.6 : sapling ? 2.4 : 6.2) * scale;
  const trunk = new THREE.Mesh(new THREE.CylinderGeometry(0.5 * scale, 1.15 * scale, trunkH, 8), bark);
  trunk.position.y = trunkH / 2;
  group.add(trunk);
  for (let i = 0; i < (sapling ? 4 : 7); i++) {
    const a = (i / (sapling ? 4 : 7)) * Math.PI * 2;
    const root = new THREE.Mesh(new THREE.CylinderGeometry(0.12 * scale, 0.35 * scale, 2.2 * scale, 5), barkLight);
    root.position.set(Math.cos(a) * 0.8 * scale, 0.4 * scale, Math.sin(a) * 0.8 * scale);
    root.rotation.z = Math.cos(a) * 0.75;
    root.rotation.x = -Math.sin(a) * 0.75;
    group.add(root);
  }
  const canopyY = trunkH + 0.4 * scale;
  for (let i = 0; i < (sapling ? 4 : 9); i++) {
    const a = (i / (sapling ? 4 : 9)) * Math.PI * 2;
    const crown = new THREE.Mesh(new THREE.IcosahedronGeometry((0.65 + (i % 3) * 0.2) * scale, 1), leaf);
    crown.position.set(Math.cos(a) * 1.35 * scale, canopyY + Math.sin(a * 2) * 0.55 * scale, Math.sin(a) * 1.35 * scale);
    crown.scale.y = 0.75;
    group.add(crown);
  }
  const heart = new THREE.Mesh(new THREE.IcosahedronGeometry((defense ? 0.65 : 0.42) * scale, 1), bloom);
  heart.name = 'pulseCore';
  heart.position.y = canopyY;
  group.add(heart);
  if (defense) {
    const turret = new THREE.Group();
    turret.name = 'turret';
    turret.position.y = canopyY - 0.35;
    for (const sx of [-1, 1]) {
      const thorn = new THREE.Mesh(new THREE.ConeGeometry(0.18, 1.8, 6), barkLight);
      thorn.rotation.x = Math.PI / 2;
      thorn.rotation.z = sx * 0.28;
      thorn.position.set(sx * 0.45, 0, 0.8);
      turret.add(thorn);
    }
    group.add(turret);
  }
  return finish(group);
}

function astralCoreAnnexes(
  shell: THREE.Material,
  dark: THREE.Material,
  blue: THREE.Material,
  violet: THREE.Material,
): THREE.Group[] {
  // top socket — Phase Barrier: two crescent crystal wings orbiting above the prism, plus a
  // faint translucent phase-shield bubble around the core.
  const barrier = new THREE.Group();
  barrier.name = 'annex-up-ad-phase-barrier';
  const wings = new THREE.Group();
  wings.name = 'spinner';
  wings.position.y = 8.6;
  for (const sx of [-1, 1]) {
    const crescent = new THREE.Mesh(new THREE.TorusGeometry(1.35, 0.16, 7, 20, Math.PI * 0.72), violet);
    crescent.position.x = sx * 2.05;
    crescent.rotation.y = sx * (Math.PI / 2);
    crescent.rotation.z = Math.PI * 0.14;
    wings.add(crescent);
    const shard = new THREE.Mesh(new THREE.OctahedronGeometry(0.3, 0), blue);
    shard.position.set(sx * 2.05, 1.0, 0);
    wings.add(shard);
  }
  barrier.add(wings);
  const shield = new THREE.Mesh(new THREE.SphereGeometry(3.8, 18, 12), luminous(0x6fd2ff, 0.35, 0.1));
  shield.name = 'pulseCore';
  shield.position.y = 4.7;
  barrier.add(shield);

  // left socket — Conduit Nexus: floating crystal cluster tied together by thin light beams.
  const nexus = new THREE.Group();
  nexus.name = 'annex-up-ad-conduit-nexus';
  const pad = new THREE.Mesh(new THREE.CylinderGeometry(1.3, 1.6, 0.3, 8), dark);
  pad.position.set(-4.15, 0.15, 0);
  nexus.add(pad);
  const nodes: [number, number, number, number][] = [
    [-4.15, 1.1, 0, 0.5],
    [-4.95, 2.0, 0.75, 0.34],
    [-3.5, 2.6, -0.85, 0.38],
    [-4.55, 3.4, -0.15, 0.3],
    [-3.95, 4.3, 0.55, 0.44],
  ];
  const up = new THREE.Vector3(0, 1, 0);
  for (let i = 0; i < nodes.length; i++) {
    const [x, y, z, r] = nodes[i];
    const crystal = new THREE.Mesh(new THREE.OctahedronGeometry(r, 0), i % 2 ? violet : blue);
    crystal.position.set(x, y, z);
    nexus.add(crystal);
    if (i > 0) {
      const [px, py, pz] = nodes[i - 1];
      const dir = new THREE.Vector3(x - px, y - py, z - pz);
      const beam = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.03, dir.length(), 4), blue);
      beam.position.set((x + px) / 2, (y + py) / 2, (z + pz) / 2);
      beam.quaternion.setFromUnitVectors(up, dir.normalize());
      nexus.add(beam);
    }
  }

  // bottom socket — Echo Observatory: rotating telescope-ring plus a ghostly hologram.
  const observatory = new THREE.Group();
  observatory.name = 'annex-up-ad-echo-observatory';
  const pedestal = new THREE.Mesh(new THREE.CylinderGeometry(0.85, 1.15, 0.5, 8), dark);
  pedestal.position.set(0, 0.25, 4.35);
  observatory.add(pedestal);
  const wheel = new THREE.Group();
  wheel.name = 'spinnerReverse';
  wheel.position.set(0, 1.35, 4.35);
  const track = new THREE.Mesh(new THREE.TorusGeometry(1.55, 0.11, 7, 22), shell);
  track.rotation.x = Math.PI / 2;
  wheel.add(track);
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2;
    const stanchion = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.09, 0.85, 5), dark);
    stanchion.position.set(Math.cos(a) * 1.55, -0.42, Math.sin(a) * 1.55);
    wheel.add(stanchion);
  }
  const scope = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.3, 2.1, 7), dark);
  scope.position.set(1.15, 0.7, 0);
  scope.rotation.z = -0.85;
  wheel.add(scope);
  const lens = new THREE.Mesh(new THREE.CylinderGeometry(0.21, 0.21, 0.08, 7), blue);
  lens.position.set(1.95, 1.4, 0);
  lens.rotation.z = -0.85;
  wheel.add(lens);
  observatory.add(wheel);
  const holo = new THREE.Mesh(new THREE.OctahedronGeometry(0.52, 0), luminous(0x9fe8ff, 0.9, 0.45));
  holo.name = 'orb';
  holo.position.set(0, 3.1, 4.35);
  observatory.add(holo);

  for (const annex of [barrier, nexus, observatory]) annex.visible = false;
  return [barrier, nexus, observatory];
}

function remainingAstralBuilding(defId: string, owner: 0 | 1, fp: Footprint): THREE.Group {
  const group = new THREE.Group();
  const shell = material(mix(0xdce5f2, OWNER_COLORS[owner], 0.14), 0.3, 0.7);
  const dark = material(0x172039, 0.38, 0.72);
  const blue = luminous(0x4dcfff, 1.45, 0.88);
  const violet = luminous(0x785cff, 1.3, 0.88);
  const wall = defId === 'b-ad-castle-wall';
  const standard = defId === 'b-ad-ritual-standard';
  const core = defId === 'b-ad-meridian-prism';
  const decoy = defId === 'b-ad-decoy';
  const w = wall ? fp.w + 0.5 : Math.max(1.4, fp.w * 0.76);
  const d = wall ? fp.h : Math.max(1.4, fp.h * 0.76);

  if (standard) {
    const base = new THREE.Mesh(new THREE.CylinderGeometry(0.9, 1.2, 0.3, 8), dark);
    base.position.y = 0.15;
    group.add(base);
    const spinner = new THREE.Group();
    spinner.name = 'spinner';
    spinner.position.y = 2.8;
    for (let i = 0; i < 4; i++) {
      const ring = new THREE.Mesh(new THREE.TorusGeometry(0.65 + i * 0.22, 0.04, 6, 18), i % 2 ? blue : shell);
      ring.rotation.set(i * 0.4, i * 0.7, 0);
      spinner.add(ring);
    }
    group.add(spinner);
    const coreGem = new THREE.Mesh(new THREE.OctahedronGeometry(0.38, 0), violet);
    coreGem.name = 'pulseCore';
    coreGem.position.y = 2.8;
    group.add(coreGem);
    return finish(group);
  }
  if (wall) {
    for (let z = -d / 2 + 0.5; z <= d / 2 - 0.5; z += 1.05) {
      const panel = new THREE.Mesh(new THREE.OctahedronGeometry(0.72, 0), (Math.round(z * 10) % 2) ? shell : dark);
      panel.position.set(0, 2.0, z);
      panel.scale.set(w * 0.7, 2.25, 0.65);
      group.add(panel);
      const seam = new THREE.Mesh(new THREE.BoxGeometry(w + 0.45, 0.055, 0.055), blue);
      seam.position.set(0, 2.0, z + 0.48);
      group.add(seam);
    }
    for (const z of [-d / 2, d / 2]) {
      const anchor = new THREE.Mesh(new THREE.CylinderGeometry(0.72, 1.05, 5.2, 6), dark);
      anchor.position.set(0, 2.6, z);
      group.add(anchor);
      const prism = new THREE.Mesh(new THREE.OctahedronGeometry(0.58, 0), violet);
      prism.position.set(0, 5.65, z);
      group.add(prism);
    }
    return finish(group);
  }
  const base = new THREE.Mesh(new THREE.CylinderGeometry(w * 0.42, w * 0.52, core ? 1.2 : 0.65, 8), dark);
  base.position.y = core ? 0.6 : 0.32;
  group.add(base);
  const coreGem = new THREE.Mesh(new THREE.OctahedronGeometry(core ? 1.45 : decoy ? 0.75 : 0.62, core ? 2 : 1), decoy ? luminous(0x86dfff, 0.55, 0.35) : core ? violet : blue);
  coreGem.name = 'pulseCore';
  coreGem.position.y = core ? 4.7 : 2.2;
  group.add(coreGem);
  const spinner = new THREE.Group();
  spinner.name = 'spinner';
  spinner.position.copy(coreGem.position);
  const rings = core ? 5 : 3;
  for (let i = 0; i < rings; i++) {
    const ring = new THREE.Mesh(new THREE.TorusGeometry((core ? 2.0 : 1.0) + i * (core ? 0.38 : 0.25), 0.06, 7, 24), i % 2 ? shell : violet);
    ring.rotation.set(Math.PI / 2 + i * 0.24, i * 0.65, i * 0.3);
    spinner.add(ring);
  }
  group.add(spinner);
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) {
    const pylon = new THREE.Mesh(new THREE.ConeGeometry(0.34, core ? 4.6 : 2.7, 4), shell);
    pylon.position.set(sx * w * 0.34, core ? 2.3 : 1.35, sz * d * 0.34);
    group.add(pylon);
  }
  if (defId === 'b-ad-aether-refinery') {
    const reverse = spinner.clone();
    reverse.name = 'spinnerReverse';
    reverse.scale.setScalar(0.72);
    group.add(reverse);
  }
  if (core) {
    for (const annex of astralCoreAnnexes(shell, dark, blue, violet)) group.add(annex);
  }
  return finish(group);
}

function remainingBuilding(defId: string, owner: 0 | 1, footprint: Footprint): THREE.Group {
  if (defId.startsWith('b-ic-')) return remainingIronBuilding(defId, owner, footprint);
  if (defId.startsWith('b-ah-')) return remainingAshenBuilding(defId, owner, footprint);
  if (defId.startsWith('b-vw-')) return remainingVerdantBuilding(defId, owner, footprint);
  return remainingAstralBuilding(defId, owner, footprint);
}

export function makeBespokeBuildingRig(defId: string, owner: 0 | 1, footprint: Footprint): THREE.Group {
  switch (defId) {
    case 'b-ic-barracks': return ironBastion(owner, footprint);
    case 'b-ic-longshot-foundry': return longshotFoundry(owner, footprint);
    case 'b-ic-siege-depot': return siegeDepot(owner, footprint);
    case 'b-ah-grave-hatchery': return graveHatchery(owner, footprint);
    case 'b-ah-blood-foundry': return bloodFoundry(owner, footprint);
    case 'b-ah-rift-altar': return riftAltar(owner, footprint);
    case 'b-vw-thorn-nursery': return thornNursery(owner, footprint);
    case 'b-vw-beast-hollow': return beastHollow(owner, footprint);
    case 'b-vw-skybloom-roost': return skybloomRoost(owner, footprint);
    case 'b-ad-precision-foundry': return precisionFoundry(owner, footprint);
    case 'b-ad-conduit-forge': return phaseCitadel(owner, footprint);
    case 'b-ad-starforge-dock': return starforgeDock(owner, footprint);
    case 'b-vw-bramblehold': return brambleholdOutpost(owner, footprint);
    case 'b-ic-field-bastion': return fieldBastion(owner, footprint);
    case 'b-ah-blood-cocoon': return bloodCocoon(owner, footprint);
    case 'b-ah-grave-beacon': return graveBeacon(owner, footprint);
    case 'b-ad-astral-relay': return astralRelay(owner, footprint);
    default: return remainingBuilding(defId, owner, footprint);
  }
}
